import { calendarDateToLocal, formatCalendarDate } from '../utils/calendarDate';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, ChevronDown, ChevronLeft, Loader2, CheckCircle, Clock,
  Wrench, ThumbsUp, Play, FileCheck, Zap, Calendar,
  User, Shield, Tag, AlertTriangle, Save,
  RefreshCw, Sparkles, DollarSign, FileSpreadsheet,
  Plus, Trash2, X, CircleDot, Circle, FileText, Upload,
  Lock, Unlock, ExternalLink, Eye, MessageSquare,
  ShieldCheck, ShieldX, FileSearch, CheckSquare,
  Send, ChevronRight, Maximize2, Minimize2, PanelLeftOpen, PanelLeftClose, Copy,
} from 'lucide-react';
import axiosInstance from '../api/axios';
import { listIssueRunOptions, pickIssueRuns } from '../api/controlTestRuns';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { isControlUuid, resolveControlUuid } from '../features/controls/functional.ts';
import { TENANT_PATHS } from '../routing/paths.ts';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import RichTextEditor, { stripHtml, toRichContent } from './RichTextEditor';

// ─── Constants ───────────────────────────────────────────────────────────────

// STAGE_PIPELINE — the 5 linear stages shown in the stepper (escalated is an overlay, not a step).
const PERIOD_OPTIONS = (() => {
  const year = new Date().getFullYear();
  const opts = [];
  for (let y = year - 2; y <= year + 1; y++) {
    opts.push({ value: `FY ${y}`, label: `FY ${y}` });
    ['Q1', 'Q2', 'Q3', 'Q4'].forEach(q => opts.push({ value: `${q} ${y}`, label: `${q} ${y}` }));
  }
  return opts;
})();

// 5 logical stages for the header stepper.
const STAGE_PIPELINE = [
  { key: 'draft',        label: 'Draft',        shortLabel: 'Draft',    icon: Clock        },
  { key: 'review',       label: 'Under Review', shortLabel: 'Review',   icon: Eye          },
  { key: 'remediation',  label: 'Remediation',  shortLabel: 'Remediate',icon: Wrench       },
  { key: 'verification', label: 'Verification', shortLabel: 'Verify',   icon: ShieldCheck  },
  { key: 'closed',       label: 'Closed',       shortLabel: 'Closed',   icon: CheckCircle  },
];

// Maps issue status → stepper stage index.
const STATUS_TO_STAGE = {
  draft:                    0,
  reviewed:                 1,
  in_management_review:     1,
  in_discussion:            1,
  rework:                   0,
  plan_accepted:            2,
  remediation_in_progress:  2,
  remediation_complete:     2,
  pending_verification:     3,
  verification_complete:    3,
  verification_failed:      3,
  closed:                   4,
  reopened:                 4,
  escalated:                0,
};

const STATUS_COLORS = {
  draft:                   'text-amber-600',
  reviewed:                'text-sky-600',
  in_management_review:    'text-blue-600',
  in_discussion:           'text-amber-600',
  rework:                  'text-orange-600',
  plan_accepted:           'text-green-600',
  remediation_in_progress: 'text-orange-600',
  remediation_complete:    'text-blue-600',
  pending_verification:    'text-indigo-600',
  verification_complete:   'text-emerald-600',
  verification_failed:     'text-red-600',
  closed:                  'text-gray-500',
  reopened:                'text-amber-600',
  escalated:               'text-purple-600',
};

const SEVERITY_CONFIG = {
  observation:            { short: 'OBS', label: 'Observation',            bg: 'bg-green-500',  text: 'text-white' },
  material_weakness:      { short: 'MW',  label: 'Material Weakness',      bg: 'bg-red-500',    text: 'text-white' },
  significant_deficiency: { short: 'SD',  label: 'Significant Deficiency', bg: 'bg-yellow-500', text: 'text-white' },
  control_deficiency:     { short: 'CD',  label: 'Control Deficiency',     bg: 'bg-blue-600',   text: 'text-white' },
};

const CLASSIFICATION_OPTIONS = [
  { value: '',                       label: 'Not classified'         },
  { value: 'observation',            label: 'Observation'            },
  { value: 'material_weakness',      label: 'Material Weakness'      },
  { value: 'significant_deficiency', label: 'Significant Deficiency' },
  { value: 'control_deficiency',     label: 'Control Deficiency'     },
];

const COSO_COMPONENTS = [
  'Control Environment',
  'Risk Assessment',
  'Control Activities',
  'Information & Communication',
  'Monitoring Activities',
];

const DEPARTMENTS = ['Finance', 'HR', 'IT', 'Operations', 'Accounting', 'Legal', 'Procurement', 'Sales', 'Inventory'];
const BASE_CATEGORIES = ['Authorization', 'Access Management', 'Segregation of Duties', 'Financial Reporting', 'Change Management', 'Vendor Management', 'Physical Security', 'Data Integrity', 'Documentation', 'Reconciliation'];
const CUSTOM_CATEGORIES_KEY = 'sox_issue_custom_categories';

const SUB_TABS_BY_STEP = {
  0: ['Issue Details', 'Remediation Plan'],
  1: ['Issue Details', 'Remediation Plan', 'Management Review'],
  2: ['Issue Details', 'Remediation Plan', 'Remediation Execution'],
  3: ['Issue Details', 'Remediation Plan', 'Verification'],
  4: ['Issue Details', 'Remediation Plan', 'Closed'],
};

const SUB_TABS_REOPENED = ['Issue Details', 'Remediation Plan', 'Reopened'];

// Per-status display config for the header badge.
const STATUS_DISPLAY = {
  draft:                   { label: 'Draft',                    icon: Clock        },
  reviewed:                { label: 'Team Manager Review',       icon: Eye          },
  in_management_review:    { label: 'Management Review',        icon: Eye          },
  in_discussion:           { label: 'In Discussion',            icon: MessageSquare},
  rework:                  { label: 'In Rework',                icon: RefreshCw    },
  plan_accepted:           { label: 'Plan Accepted',            icon: ThumbsUp     },
  remediation_in_progress: { label: 'Remediation In Progress',  icon: Wrench       },
  remediation_complete:    { label: 'Remediation Complete',     icon: CheckSquare  },
  pending_verification:    { label: 'Pending Verification',     icon: FileSearch   },
  verification_complete:   { label: 'Verification Complete',    icon: ShieldCheck  },
  verification_failed:     { label: 'Verification Failed',      icon: ShieldX      },
  closed:                  { label: 'Closed',                   icon: CheckCircle  },
  reopened:                { label: 'Reopened',                 icon: RefreshCw    },
  escalated:               { label: 'Escalated',                icon: Zap          },
};

const MW_INDICATORS = [
  { id: 'restatement',                label: 'Identification of a financial statement restatement',                                    ref: 'AS 2201.69(a)' },
  { id: 'auditor_adjustment',         label: 'Material misstatement not initially identified by management',                           ref: 'AS 2201.69(b)' },
  { id: 'ineffective_audit_committee',label: 'Ineffective oversight by the audit committee',                                           ref: 'AS 2201.69(c)' },
  { id: 'material_weakness_prior',    label: 'Material weakness identified in the prior year',                                         ref: 'AS 2201.69(d)' },
  { id: 'ineffective_risk_assessment',label: 'Ineffective risk assessment process',                                                    ref: 'AS 2201.69(e)' },
  { id: 'senior_management_fraud',    label: 'Evidence of fraud by senior management',                                                 ref: 'AS 2201.69(f)' },
  { id: 'ineffective_monitoring',     label: 'Ineffective monitoring of controls',                                                     ref: 'AS 2201.69(g)' },
  { id: 'complex_transactions',       label: 'Significant deficiencies in complex or unusual transactions',                            ref: 'AS 2201.69(h)' },
  { id: 'period_end_reporting',       label: 'Significant deficiency in the period-end financial reporting process',                   ref: 'AS 2201.69(i)' },
];

const DEFAULT_MW_INDICATORS = MW_INDICATORS.map(i => ({ indicator_id: i.id, label: i.label, checked: false, rationale: '' }));


const AP_STATUS_CONFIG = {
  not_started: { label: 'Not Started', icon: Circle,      color: 'text-gray-500',  bg: 'bg-gray-100'   },
  in_progress:  { label: 'In Progress', icon: CircleDot,   color: 'text-blue-600',  bg: 'bg-blue-50'    },
  completed:    { label: 'Completed',   icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50'   },
};

const EMPTY_AP_FORM = { title: '', corrective_action: '', owner_name: '', owner_role: '', due_date: '', ticket_reference: '' };

// Extract corrective_action from description (supports legacy JSON format and plain text).
function parseApDescription(desc) {
  if (!desc) return '';
  try {
    const p = JSON.parse(desc);
    if (p.corrective_action !== undefined) return p.corrective_action || '';
  } catch {}
  return desc;
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseOverviewData(raw) {
  if (!raw || typeof raw !== 'object') return {};
  return raw;
}

function MetaLabel({ children }) {
  return <p className="text-xs text-gray-500 font-medium mb-1">{children}</p>;
}

// ─── Auto-growing textarea ───────────────────────────────────────────────────


// ─── Component ───────────────────────────────────────────────────────────────

export default function IssueDetail() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { id }    = useParams();
  const { selectedAuditYearId } = useAuditYear();

  const [issue,          setIssue]          = useState(location.state?.issue || null);
  const [loading,        setLoading]        = useState(!location.state?.issue);
  const [activeStep,     setActiveStep]     = useState(0);
  const [activeSubTab,   setActiveSubTab]   = useState('Issue Details');
  const [showEvidencePanel, setShowEvidencePanel] = useState(false);
  const [showMemoPanel,     setShowMemoPanel]     = useState(false);
  const [memoExpanded,      setMemoExpanded]      = useState(false);
  const [showIssueContext,  setShowIssueContext]  = useState(true);
  const [advancing,      setAdvancing]      = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [saveMsg,        setSaveMsg]        = useState('');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [financialFile,  setFinancialFile]  = useState(null);
  const financialFileRef = useRef(null);
  const allOrgIssuesYearRef = useRef(null); // year the current allOrgIssues list was fetched for

  // Remediation tab
  const [actionPlans,    setActionPlans]    = useState([]);
  const [apLoading,      setApLoading]      = useState(false);
  const [showApForm,     setShowApForm]     = useState(false);
  const [apForm,         setApForm]         = useState(EMPTY_AP_FORM);
  const [apSubmitting,   setApSubmitting]   = useState(false);
  const [deletingAp,     setDeletingAp]     = useState(null);
  const [remediationData, setRemediationData] = useState({ plan: '', target_date: '', owner_name: '', owner_role: '' });

  // Evidence & Artifacts
  const [evidence,        setEvidence]        = useState([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const evidenceFileRef = useRef(null);
  const memoFileRef = useRef(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [apEvidenceRef, setApEvidenceRef] = useState(null); // action_plan_id scoping for upload

  // Completion modal (for "Mark Complete" on AP items)
  const [completionModal, setCompletionModal] = useState(null); // { apId, apTitle }
  const [completionNotes, setCompletionNotes] = useState('');
  const [completionSubmitting, setCompletionSubmitting] = useState(false);

  // Verification panel state
  const [verifyData, setVerifyData] = useState({ manual_notes: '', verifying: false });
  const [linkedTestRuns, setLinkedTestRuns] = useState([]); // test runs available for linking
  const [linkedVerifRun, setLinkedVerifRun] = useState(null); // selected verification run
  const [loadingTestRuns, setLoadingTestRuns] = useState(false);
  const [verifMethod, setVerifMethod] = useState('test_run'); // 'test_run' | 'comment'
  const [verifComment, setVerifComment] = useState('');

  // Close Issue modal
  const [closeModal, setCloseModal]   = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closingIssue, setClosingIssue] = useState(false);

  // Clone
  const [cloningIssue, setCloningIssue] = useState(false);
  const [showCloneConfirm, setShowCloneConfirm] = useState(false);

  // Re-open modal
  const [reopenModal, setReopenModal]     = useState(false);
  const [reopenReason, setReopenReason]   = useState('');
  const [reopeningIssue, setReopeningIssue] = useState(false);

  // Requires Discussion modal (to capture initial note)
  const [reqDiscModal, setReqDiscModal]   = useState(false);
  const [reqDiscNote, setReqDiscNote]     = useState('');
  const [reqDiscSubmitting, setReqDiscSubmitting] = useState(false);

  // Discussion thread note input (used when status is in_discussion)
  const [discussionNote, setDiscussionNote] = useState('');
  const [addingNote, setAddingNote]         = useState(false);

  // Management Response tab
  const [mgmtData, setMgmtData] = useState({
    response_text: '', responder_name: '', responder_title: '',
    response_date: '', acceptance: '', discussion_thread: [],
  });

  // Memo tab
  const [memoData, setMemoData] = useState({
    memo_to: '', memo_from: 'Internal Audit', memo_subject: '',
    condition: '', criteria: '', cause: '', consequence: '', corrective_action: '',
    risk_factors: '', exposed_amounts: '', magnitude_assessment: '',
    compensating_controls: '', recommendation: '',
    mw_indicators: DEFAULT_MW_INDICATORS,
    reportable_to_sec: null,
  });
  const [memoLoading,      setMemoLoading]      = useState(false);
  const [pdfExporting,     setPdfExporting]     = useState(false);
  const [memoExportError,  setMemoExportError]  = useState('');
  const [docxExporting,    setDocxExporting]    = useState(false);
  const [summaryGenerating,     setSummaryGenerating]     = useState(false);
  const [rootCauseLoading,      setRootCauseLoading]      = useState(false);
  const [remediationGenerating, setRemediationGenerating] = useState(false);
  const [aiWarning,             setAiWarning]             = useState('');
  const [lockedSections,        setLockedSections]        = useState({});

  const toggleLock = (section) => setLockedSections(prev => ({ ...prev, [section]: !prev[section] }));
  const isLocked   = (section) => !!lockedSections[section];

  // Send to Stakeholders modal
  const [showSendModal,   setShowSendModal]   = useState(false);
  const [sendEmails,      setSendEmails]      = useState([]);
  const [sendEmailInput,  setSendEmailInput]  = useState('');
  const [sendNote,        setSendNote]        = useState('');
  const [sending,         setSending]         = useState(false);
  const [sendError,       setSendError]       = useState('');

  // Source tab
  const [sourceData, setSourceData] = useState({ related_issue_ids: [] });
  const [allOrgIssues,   setAllOrgIssues]   = useState([]);

  // Linked controls (junction table)
  const [linkedControls,        setLinkedControls]        = useState([]);
  const [allControls,           setAllControls]           = useState([]);
  const [showControlsSelect,    setShowControlsSelect]    = useState(false);
  const [savingControls,        setSavingControls]        = useState(false);
  const [availableRunsByControl, setAvailableRunsByControl] = useState({});
  const controlsDropdownRef = useRef(null);

  // Recurring auto-detect
  const [priorIssues,     setPriorIssues]     = useState([]);
  const [showPriorIssues, setShowPriorIssues] = useState(false);

  // Dynamic categories
  const [customCategories, setCustomCategories] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_CATEGORIES_KEY) || '[]'); } catch { return []; }
  });
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');

  // Mandatory field validation
  const [validationErrors, setValidationErrors] = useState({});

  // Editable metadata fields
  const [fields, setFields] = useState({
    classification: '', department: '', category: '',
    assignee_name: '', assignee_role: '', period: '', is_repeat: false, issue_type: 'operating_effectiveness',
  });
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');

  // Overview text blobs
  const [overview, setOverview] = useState({
    summary_of_deficiency: '',
    root_cause_analysis: '',
    impact: '',
    financial_impact: '',
    coso_components: [],
    linked_controls: [],
    linked_test_runs: [],
  });

  // ── Load ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Always fetch fresh data so junction-table controls are included.
    // If we have state data, pre-fill immediately so the UI isn't blank while loading.
    if (location.state?.issue) { hydrateFromIssue(location.state.issue); }
    fetchIssue();
  }, []);

  const fetchIssue = async () => {
    try {
      const res = await axiosInstance.get(`/api/sox-issues/${id}`);
      setIssue(res.data);
      hydrateFromIssue(res.data);
      const stageIdx = STATUS_TO_STAGE[res.data.status] ?? 0;
      setActiveStep(stageIdx);
      if (stageIdx === 0) setActiveSubTab('Issue Details');
      else if (stageIdx === 1) setActiveSubTab(
        ['in_management_review', 'in_discussion'].includes(res.data.status) ? 'Management Review' : 'Issue Details'
      );
      else if (stageIdx === 2) setActiveSubTab('Remediation Execution');
      else if (stageIdx === 3) setActiveSubTab('Verification');
      else if (stageIdx === 4) setActiveSubTab(res.data.status === 'reopened' ? 'Reopened' : 'Closed');
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const hydrateFromIssue = (data) => {
    setFields({
      classification: data.classification || '',
      department:     data.department     || '',
      category:       data.category       || '',
      assignee_name:  data.assignee_name  || '',
      assignee_role:  data.assignee_role  || '',
      period:         data.period         || '',
      is_repeat:      data.is_repeat      || false,
      issue_type:     data.issue_type     || 'operating_effectiveness',
    });
    // Hydrate controls from junction table
    if (Array.isArray(data.controls)) {
      setLinkedControls(data.controls);
    }
    setTitleValue(data.title || '');
    const od = parseOverviewData(data.overview_data);
    setOverview({
      summary_of_deficiency: od.summary_of_deficiency || data.summary_of_deficiency || '',
      root_cause_analysis:   od.root_cause_analysis   || '',
      impact:                od.impact                || '',
      financial_impact:      od.financial_impact      || '',
      coso_components:       od.coso_components       || [],
      linked_controls:       od.linked_controls       || [],
      linked_test_runs:      od.linked_test_runs      || [],
    });
    const rd = parseOverviewData(data.remediation_data);
    setRemediationData({
      plan:       rd.plan       || '',
      target_date: rd.target_date || '',
      owner_name: rd.owner_name || '',
      owner_role: rd.owner_role || '',
    });
    const md = parseOverviewData(data.management_response_data);
    setMgmtData({
      response_text:     md.response_text     || '',
      responder_name:    md.responder_name    || '',
      responder_title:   md.responder_title   || '',
      response_date:     md.response_date     || '',
      acceptance:        md.acceptance        || '',
      discussion_thread: Array.isArray(md.discussion_thread) ? md.discussion_thread : [],
    });

    const memo = parseOverviewData(data.memo_data);
    setMemoData({
      memo_to:              memo.memo_to              || '',
      memo_from:            memo.memo_from            || 'Internal Audit',
      memo_subject:         memo.memo_subject         || data.title || '',
      condition:            memo.condition            || '',
      criteria:             memo.criteria             || '',
      cause:                memo.cause                || '',
      consequence:          memo.consequence          || '',
      corrective_action:    memo.corrective_action    || '',
      risk_factors:         memo.risk_factors         || '',
      exposed_amounts:      memo.exposed_amounts      || '',
      magnitude_assessment: memo.magnitude_assessment || '',
      compensating_controls:memo.compensating_controls|| '',
      recommendation:       memo.recommendation       || '',
      mw_indicators:        memo.mw_indicators?.length ? memo.mw_indicators : DEFAULT_MW_INDICATORS,
      reportable_to_sec:    memo.reportable_to_sec    ?? null,
    });

    const src = parseOverviewData(data.source_data);
    setSourceData({
      related_issue_ids: src.related_issue_ids || [],
    });
  };

  useEffect(() => {
    if (activeStep === 2 || activeSubTab === 'Remediation Plan') {
      if (actionPlans.length === 0 && !apLoading) fetchActionPlans();
    }
    if (activeSubTab === 'Issue Details') {
      // Refetch whenever the year to use changes (e.g. this runs once before the issue
      // has loaded, using the selected year, then again with the issue's own year).
      const issueScopeYearId = issue?.audit_year_id || selectedAuditYearId || '';
      if (allOrgIssuesYearRef.current !== issueScopeYearId) {
        allOrgIssuesYearRef.current = issueScopeYearId;
        const issuesParams = issueScopeYearId ? { audit_year_id: issueScopeYearId } : {};
        axiosInstance.get('/api/sox-issues', { params: issuesParams }).then(res => {
          if (allOrgIssuesYearRef.current !== issueScopeYearId) return;
          setAllOrgIssues((res.data.issues || []).filter(i => i.id !== id));
        }).catch(() => {});
      }
    }
    if (activeSubTab === 'Issue Details' && allControls.length === 0) {
      axiosInstance.get('/api/sox-controls').then(res => {
        setAllControls(res.data.controls || res.data || []);
      }).catch(() => {});
    }
  }, [activeStep, activeSubTab, issue?.audit_year_id, selectedAuditYearId]);

  useEffect(() => {
    if (showEvidencePanel && evidence.length === 0 && !evidenceLoading) fetchEvidence();
  }, [showEvidencePanel]);

  // ── Save helpers ─────────────────────────────────────────────────────────────

  const showSaved = () => { setSaveMsg('Saved'); setTimeout(() => setSaveMsg(''), 2000); };

  const saveFields = useCallback(async (patch) => {
    setSaving(true);
    try { await axiosInstance.put(`/api/sox-issues/${id}`, patch); showSaved(); }
    catch (e) { console.error('Save failed:', e); }
    finally { setSaving(false); }
  }, [id]);

  const saveOverview = useCallback(async (next) => {
    setSaving(true);
    try { await axiosInstance.put(`/api/sox-issues/${id}`, { overview_data: next }); showSaved(); }
    catch (e) { console.error('Overview save failed:', e); }
    finally { setSaving(false); }
  }, [id]);

  const handleFieldBlur = (name) => saveFields({ [name]: fields[name] || null });

  const handleOverviewBlur = () => saveOverview(overview);

  // Group linked_test_runs by control_id for display
  const testRunsByControl = useMemo(() => {
    const groups = {};
    (overview.linked_test_runs || []).forEach(run => {
      const cid = run.control_id || run; // handle legacy string format
      const key = typeof run === 'string' ? run.split('|')[0] : run.control_id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(typeof run === 'string' ? { run_number: Number(run.split('|')[1]) } : run);
    });
    return groups;
  }, [overview.linked_test_runs]);

  const handleTitleBlur = () => {
    setEditingTitle(false);
    if (titleValue.trim() && titleValue !== issue?.title) {
      setIssue(prev => ({ ...prev, title: titleValue.trim() }));
      saveFields({ title: titleValue.trim() });
    }
  };

  const toggleCoso = (component) => {
    const next = overview.coso_components.includes(component)
      ? overview.coso_components.filter(c => c !== component)
      : [...overview.coso_components, component];
    const nextOverview = { ...overview, coso_components: next };
    setOverview(nextOverview);
    if (next.length > 0) setValidationErrors(v => ({ ...v, coso_components: false }));
    saveOverview(nextOverview);
  };

  // ── Action Plan handlers ────────────────────────────────────────────────────

  const fetchActionPlans = async () => {
    setApLoading(true);
    try {
      const res = await axiosInstance.get(`/api/sox-issues/${id}/action-plans`);
      setActionPlans(res.data.action_plans || []);
    } catch (e) { console.error(e); }
    finally { setApLoading(false); }
  };

  const handleCreateAp = async (e) => {
    e.preventDefault();
    if (!apForm.title.trim()) return;
    setApSubmitting(true);
    try {
      const body = {
        title:            apForm.title.trim(),
        description:      apForm.corrective_action.trim() || undefined,
        owner_name:       apForm.owner_name       || undefined,
        owner_role:       apForm.owner_role       || undefined,
        due_date:         apForm.due_date         || undefined,
        ticket_reference: apForm.ticket_reference || undefined,
      };
      const res = await axiosInstance.post(`/api/sox-issues/${id}/action-plans`, body);
      setActionPlans(prev => [...prev, res.data]);
      setShowApForm(false);
      setApForm(EMPTY_AP_FORM);
    } catch (e) { console.error(e); }
    finally { setApSubmitting(false); }
  };

  const handleApStatusChange = async (apId, newStatus, completionNotesText) => {
    try {
      const body = { status: newStatus };
      if (completionNotesText) body.completion_notes = completionNotesText;
      const res = await axiosInstance.put(`/api/sox-issues/${id}/action-plans/${apId}/status`, body);
      setActionPlans(prev => prev.map(p =>
        p.id === apId ? { ...p, status: newStatus, completion_notes: completionNotesText || p.completion_notes } : p
      ));
      // When all APs done, auto-advance status to remediation_complete
      if (res.data?.all_completed) {
        try {
          await axiosInstance.put(`/api/sox-issues/${id}/status`, { status: 'remediation_complete' });
          setIssue(prev => ({ ...prev, status: 'remediation_complete', _allApsDone: true }));
        } catch (e) { console.error('Failed to advance to remediation_complete:', e); }
      }
    } catch (e) { console.error(e); }
  };

  const openCompletionModal = (ap) => {
    setCompletionModal({ apId: ap.id, apTitle: ap.title });
    setCompletionNotes('');
  };

  const submitCompletion = async () => {
    if (!completionNotes.trim()) return;
    setCompletionSubmitting(true);
    await handleApStatusChange(completionModal.apId, 'completed', completionNotes.trim());
    setCompletionSubmitting(false);
    setCompletionModal(null);
  };

  // Issue lifecycle CTA handlers
  const handleIssueAdvance = async (newStatus, extraParams = {}) => {
    try {
      await axiosInstance.put(`/api/sox-issues/${id}/status`, { status: newStatus, ...extraParams });
      const patch = { status: newStatus, _allApsDone: false };
      if (newStatus === 'in_discussion') patch.is_escalated = true;
      setIssue(prev => ({ ...prev, ...patch }));
      // Auto-navigate to appropriate step
      if (newStatus === 'reviewed') { setActiveStep(1); setActiveSubTab('Issue Details'); }
      else if (newStatus === 'in_management_review') { setActiveStep(1); setActiveSubTab('Management Review'); }
      else if (newStatus === 'plan_accepted') { setActiveStep(2); setActiveSubTab('Remediation Execution'); }
      showSaved();
    } catch (e) {
      const msg = resolve403ErrorMessage(e, e.response?.data?.error || 'Failed to update status');
      setAiWarning(msg);
      setTimeout(() => setAiWarning(''), 4000);
    }
  };

  const handleCloseIssue = async () => {
    if (!closeReason.trim()) return;
    setClosingIssue(true);
    try {
      await axiosInstance.put(`/api/sox-issues/${id}/status`, { status: 'closed', closing_reason: closeReason.trim() });
      setIssue(prev => {
        const sd = prev.source_data || {};
        const prevHistory = sd.closing_history || [];
        const newEntry = { reason: closeReason.trim(), closed_at: new Date().toISOString() };
        const newHistory = prevHistory.length === 0 && sd.closing_reason
          ? [{ reason: sd.closing_reason }, newEntry]
          : [...prevHistory, newEntry];
        return { ...prev, status: 'closed', source_data: { ...sd, closing_reason: closeReason.trim(), closing_history: newHistory } };
      });
      setCloseModal(false);
      setCloseReason('');
      setActiveStep(4);
      setActiveSubTab('Closed');
      showSaved();
    } catch (e) {
      const msg = resolve403ErrorMessage(e, e.response?.data?.error || 'Failed to close issue');
      setAiWarning(msg);
      setTimeout(() => setAiWarning(''), 4000);
    } finally { setClosingIssue(false); }
  };

  const handleRequiresDiscussion = async () => {
    if (!reqDiscNote.trim()) return;
    setReqDiscSubmitting(true);
    try {
      await axiosInstance.put(`/api/sox-issues/${id}/status`, { status: 'in_discussion', note: reqDiscNote.trim() });
      const newEntry = { timestamp: new Date().toISOString(), note: reqDiscNote.trim() };
      setIssue(prev => ({ ...prev, status: 'in_discussion', is_escalated: true }));
      setMgmtData(m => ({ ...m, discussion_thread: [...(m.discussion_thread || []), newEntry] }));
      setReqDiscModal(false);
      setReqDiscNote('');
      showSaved();
    } catch (e) {
      const msg = resolve403ErrorMessage(e, e.response?.data?.error || 'Failed to update status');
      setAiWarning(msg);
      setTimeout(() => setAiWarning(''), 4000);
    } finally { setReqDiscSubmitting(false); }
  };

  const handleAddDiscussionNote = async () => {
    if (!discussionNote.trim()) return;
    setAddingNote(true);
    const newEntry = { timestamp: new Date().toISOString(), note: discussionNote.trim() };
    const updated = { ...mgmtData, discussion_thread: [...(mgmtData.discussion_thread || []), newEntry] };
    try {
      await axiosInstance.put(`/api/sox-issues/${id}`, { management_response_data: updated });
      setMgmtData(updated);
      setDiscussionNote('');
    } catch (e) { console.error(e); }
    finally { setAddingNote(false); }
  };

  const handleCloneIssue = async () => {
    setCloningIssue(true);
    try {
      const res = await axiosInstance.post(`/api/sox-issues/${id}/clone`);
      navigate(TENANT_PATHS.ISSUE_DETAIL.replace(':id', res.data.id), { state: { issue: res.data } });
    } catch (e) {
      const msg = resolve403ErrorMessage(e, e.response?.data?.error || 'Failed to clone issue');
      setAiWarning(msg);
      setTimeout(() => setAiWarning(''), 4000);
    } finally { setCloningIssue(false); }
  };

  const handleReopenIssue = async () => {
    if (!reopenReason.trim()) return;
    setReopeningIssue(true);
    try {
      await axiosInstance.put(`/api/sox-issues/${id}/status`, { status: 'reopened', reopen_reason: reopenReason.trim() });
      setIssue(prev => ({ ...prev, status: 'reopened', source_data: { ...(prev.source_data || {}), reopen_reason: reopenReason.trim() } }));
      setReopenModal(false);
      setReopenReason('');
      setActiveStep(4);
      setActiveSubTab('Reopened');
      showSaved();
    } catch (e) {
      const msg = resolve403ErrorMessage(e, e.response?.data?.error || 'Failed to reopen issue');
      setAiWarning(msg);
      setTimeout(() => setAiWarning(''), 4000);
    } finally { setReopeningIssue(false); }
  };

  const handleReopenRemediation = async () => {
    try {
      await axiosInstance.put(`/api/sox-issues/${id}/status`, { status: 'remediation_in_progress' });
      setIssue(prev => ({ ...prev, status: 'remediation_in_progress', _allApsDone: false }));
      setLinkedVerifRun(null);
      setActiveStep(2);
      setActiveSubTab('Remediation Execution');
      showSaved();
    } catch (e) { console.error(e); }
  };

  const handleReopenFromVerification = handleReopenRemediation;

  const handleProceedToVerification = async () => {
    setAdvancing(true);
    try {
      if (issue.status !== 'remediation_complete') {
        await axiosInstance.put(`/api/sox-issues/${id}/status`, { status: 'remediation_complete' });
      }
      await axiosInstance.put(`/api/sox-issues/${id}/status`, { status: 'pending_verification' });
      setIssue(prev => ({ ...prev, status: 'pending_verification' }));
      setActiveStep(3);
      setActiveSubTab('Verification');
      showSaved();
    } catch (e) {
      const msg = resolve403ErrorMessage(e, e.response?.data?.error || 'Failed to update status');
      setAiWarning(msg);
      setTimeout(() => setAiWarning(''), 4000);
    } finally { setAdvancing(false); }
  };

  const handleStepClick = (idx) => {
    setActiveStep(idx);
    if (idx === 0) setActiveSubTab('Issue Details');
    else if (idx === 1) setActiveSubTab(
      ['in_management_review', 'in_discussion'].includes(issue?.status) ? 'Management Review' : 'Issue Details'
    );
    else if (idx === 2) setActiveSubTab('Remediation Execution');
    else if (idx === 3) setActiveSubTab('Verification');
    else if (idx === 4) setActiveSubTab('Closed');
  };

  // Load available test runs for the linked controls (for verification linking)
  const loadTestRunsForVerification = async () => {
    const linked = linkedControls[0];
    const controlUuid = resolveControlUuid(
      linked || (issue ? { sox_control_id: issue.sox_control_id, id: issue.sox_control_id } : null),
      linked?.sox_control_id || issue?.sox_control_id
    );
    if (!controlUuid || !isControlUuid(controlUuid)) {
      setLinkedTestRuns([]);
      return;
    }
    setLoadingTestRuns(true);
    try {
      // An issue belongs to one audit year: only offer that year's test runs.
      const issueYearId = issue?.audit_year_id || selectedAuditYearId;
      // Control-testing runs (test_runs) of that year that have results.
      setLinkedTestRuns(await listIssueRunOptions(controlUuid, issueYearId));
    } catch (e) {
      console.error('loadTestRunsForVerification:', e);
      setLinkedTestRuns([]);
    } finally { setLoadingTestRuns(false); }
  };

  const handleDeleteAp = async (apId) => {
    setDeletingAp(apId);
    try {
      await axiosInstance.delete(`/api/sox-issues/${id}/action-plans/${apId}`);
      setActionPlans(prev => prev.filter(p => p.id !== apId));
    } catch (e) { console.error(e); }
    finally { setDeletingAp(null); }
  };

  const saveRemediationData = async (next) => {
    setSaving(true);
    try { await axiosInstance.put(`/api/sox-issues/${id}`, { remediation_data: next }); showSaved(); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const saveMgmtData = async (next) => {
    setSaving(true);
    try { await axiosInstance.put(`/api/sox-issues/${id}`, { management_response_data: next }); showSaved(); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  // ── Evidence handlers ────────────────────────────────────────────────────────

  const fetchEvidence = async () => {
    setEvidenceLoading(true);
    try {
      const res = await axiosInstance.get(`/api/sox-issues/${id}/evidence`);
      setEvidence(res.data.evidence || []);
    } catch (e) { console.error(e); }
    finally { setEvidenceLoading(false); }
  };

  const handleEvidenceUpload = async (file, actionPlanId) => {
    if (!file) return;
    setUploadingEvidence(true);
    try {
      const body = {
        file_ref:      file.name,
        filename:      file.name,
        file_type:     file.type || undefined,
        file_size:     file.size || undefined,
        action_plan_id: actionPlanId || undefined,
      };
      const res = await axiosInstance.post(`/api/sox-issues/${id}/evidence`, body);
      setEvidence(prev => [...prev, res.data]);
    } catch (e) { console.error(e); }
    finally { setUploadingEvidence(false); setApEvidenceRef(null); }
  };

  const handleDeleteEvidence = async (fileId) => {
    try {
      await axiosInstance.delete(`/api/sox-issues/${id}/evidence/${fileId}`);
      setEvidence(prev => prev.filter(f => f.id !== fileId));
    } catch (e) { console.error(e); }
  };

  const [pendingMemoFile,  setPendingMemoFile]  = useState(null);
  const [parsingMemoFile,  setParsingMemoFile]  = useState(false);
  const [parseError,       setParseError]       = useState('');

  // Called when the file input fires — just stage the file for confirmation.
  const handleMemoFileSelect = (file) => {
    if (!file) return;
    setParseError('');
    setPendingMemoFile(file);
  };

  // Called after the user confirms in the dialog.
  const processMemoFileUpload = async () => {
    if (!pendingMemoFile) return;
    setParsingMemoFile(true);
    setParseError('');
    try {
      // 1. Parse fields from the document via AI service
      const formData = new FormData();
      formData.append('file', pendingMemoFile);
      const parseRes = await axiosInstance.post('/api/issues/parse-memo-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const parsed = parseRes.data;

      // 2. Merge non-null parsed fields into memoData and save
      const next = { ...memoData };
      const fields_to_merge = [
        'memo_to', 'memo_from', 'memo_subject',
        'condition', 'criteria', 'cause', 'consequence', 'corrective_action',
        'risk_factors', 'exposed_amounts', 'magnitude_assessment',
        'compensating_controls', 'recommendation',
      ];
      const richTextFields = new Set(['condition','criteria','cause','consequence','corrective_action','risk_factors','exposed_amounts','magnitude_assessment','compensating_controls','recommendation']);
      fields_to_merge.forEach(k => { if (parsed[k] != null) next[k] = richTextFields.has(k) ? toRichContent(parsed[k]) : parsed[k]; });
      setMemoData(next);
      await saveMemoData(next);

      // 3. Register the file as a memo evidence record
      const body = {
        file_ref:  pendingMemoFile.name,
        filename:  pendingMemoFile.name,
        file_type: 'memo',
        file_size: pendingMemoFile.size || undefined,
      };
      const evRes = await axiosInstance.post(`/api/sox-issues/${id}/evidence`, body);
      setEvidence(prev => [...prev, evRes.data]);

      setPendingMemoFile(null);
    } catch (e) {
      console.error('[Parse memo]', e);
      setParseError(resolve403ErrorMessage(e, 'Could not extract fields from the file. Check the format and try again.'));
    } finally {
      setParsingMemoFile(false);
    }
  };

  // ── Memo + Source save helpers ──────────────────────────────────────────────

  const saveMemoData = async (next) => {
    setSaving(true);
    try { await axiosInstance.put(`/api/sox-issues/${id}`, { memo_data: next }); showSaved(); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const saveSourceData = async (next) => {
    setSaving(true);
    try { await axiosInstance.put(`/api/sox-issues/${id}`, { source_data: next }); showSaved(); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  // Toggle a MW indicator and auto-save
  const toggleMwIndicator = (indicatorId) => {
    const next = {
      ...memoData,
      mw_indicators: memoData.mw_indicators.map(ind =>
        ind.indicator_id === indicatorId ? { ...ind, checked: !ind.checked } : ind
      ),
    };
    setMemoData(next);
    saveMemoData(next);
  };

  const updateMwRationale = (indicatorId, rationale) => {
    setMemoData(m => ({
      ...m,
      mw_indicators: m.mw_indicators.map(ind =>
        ind.indicator_id === indicatorId ? { ...ind, rationale } : ind
      ),
    }));
  };

  const saveMwRationale = () => saveMemoData(memoData);

  // ── AI Draft Memo ────────────────────────────────────────────────────────────

  const handleAIDraftMemo = async () => {
    setMemoLoading(true);
    try {
      const body = {
        issue_id:              id,
        summary_of_deficiency: stripHtml(overview.summary_of_deficiency || issue.summary_of_deficiency || ''),
        condition_text:        stripHtml(overview.root_cause_analysis) || undefined,
        root_cause_analysis:   stripHtml(overview.root_cause_analysis) || undefined,
        classification:        fields.classification         || undefined,
        financial_impact:      overview.financial_impact     || undefined,
        remediation_plan:      stripHtml(remediationData.plan) || undefined,
        management_response_text:    stripHtml(mgmtData.response_text) || undefined,
        management_response_position: mgmtData.acceptance    || undefined,
      };
      const res = await axiosInstance.post('/api/issues/ai-draft-memo', body);
      const d = res.data;
      const next = {
        ...memoData,
        condition:             d.condition             ? toRichContent(d.condition)             : memoData.condition,
        criteria:              d.criteria              ? toRichContent(d.criteria)              : memoData.criteria,
        cause:                 d.cause                 ? toRichContent(d.cause)                 : memoData.cause,
        consequence:           d.consequence           ? toRichContent(d.consequence)           : memoData.consequence,
        corrective_action:     d.corrective_action     ? toRichContent(d.corrective_action)     : memoData.corrective_action,
        risk_factors:          d.risk_factors          ? toRichContent(d.risk_factors)          : memoData.risk_factors,
        exposed_amounts:       d.exposed_amounts       ? toRichContent(d.exposed_amounts)       : memoData.exposed_amounts,
        magnitude_assessment:  d.magnitude_assessment  ? toRichContent(d.magnitude_assessment)  : memoData.magnitude_assessment,
        compensating_controls: d.compensating_controls ? toRichContent(d.compensating_controls) : memoData.compensating_controls,
        recommendation:        d.recommendation        ? toRichContent(d.recommendation)        : memoData.recommendation,
        mw_indicators:         d.mw_indicators?.length ? d.mw_indicators.map(ind => ({
          indicator_id: ind.indicator_id,
          label:        ind.label,
          checked:      ind.checked,
          rationale:    ind.rationale || '',
        })) : memoData.mw_indicators,
        reportable_to_sec: d.reportable_to_sec ?? memoData.reportable_to_sec,
      };
      setMemoData(next);
      await saveMemoData(next);
    } catch (e) {
      console.error('[AI Draft Memo]', e);
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : (detail || e?.message || 'AI memo draft failed. Try again.');
      setAiWarning(resolve403ErrorMessage(e, msg));
      setTimeout(() => setAiWarning(''), 8000);
    }
    finally { setMemoLoading(false); }
  };

  // ── Export Memo PDF ──────────────────────────────────────────────────────────

  const _buildExportBody = () => ({
    memo_to:               memoData.memo_to,
    memo_from:             memoData.memo_from,
    memo_subject:          memoData.memo_subject,
    control_id:            issue?.control_id  || undefined,
    issue_ref:             issue?.issue_id    || undefined,
    classification:        fields.classification || undefined,
    financial_impact:      overview.financial_impact || undefined,
    condition:             stripHtml(memoData.condition),
    criteria:              stripHtml(memoData.criteria),
    cause:                 stripHtml(memoData.cause),
    consequence:           stripHtml(memoData.consequence),
    corrective_action:     stripHtml(memoData.corrective_action),
    risk_factors:          stripHtml(memoData.risk_factors),
    exposed_amounts:       stripHtml(memoData.exposed_amounts),
    magnitude_assessment:  stripHtml(memoData.magnitude_assessment),
    compensating_controls: stripHtml(memoData.compensating_controls),
    recommendation:        stripHtml(memoData.recommendation),
    mw_indicators:         memoData.mw_indicators,
    reportable_to_sec:     memoData.reportable_to_sec,
    reasoning:             '',
    confidence:            1.0,
  });

  const handleExportMemoPdf = async () => {
    if (!memoData.memo_to?.trim()) {
      setMemoExportError('"To" is required. Fill in the recipient in the Memo Header.');
      setTimeout(() => setMemoExportError(''), 5000);
      return;
    }
    setMemoExportError('');
    setPdfExporting(true);
    try {
      const res = await axiosInstance.post('/api/issues/export-memo-pdf', _buildExportBody(), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${issue.issue_id}_memo.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 150);
    } catch (e) { console.error('[Export PDF]', e); }
    finally { setPdfExporting(false); }
  };

  const handleExportMemoDocx = async () => {
    if (!memoData.memo_to?.trim()) {
      setMemoExportError('"To" is required. Fill in the recipient in the Memo Header.');
      setTimeout(() => setMemoExportError(''), 5000);
      return;
    }
    setMemoExportError('');
    setDocxExporting(true);
    try {
      const res = await axiosInstance.post('/api/issues/export-memo-docx', _buildExportBody(), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${issue.issue_id}_memo.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 150);
    } catch (e) { console.error('[Export Word]', e); }
    finally { setDocxExporting(false); }
  };

  // ── AI Generate Summary ──────────────────────────────────────────────────────

  const handleGenerateSummary = async () => {
    setSummaryGenerating(true);
    setAiWarning('');
    try {
      const controlIds = linkedControls.map(c => c.control_id || c.sox_control_id).filter(Boolean);
      const body = {
        issue_id:        id,
        classification:  fields.classification || undefined,
        category:        fields.category       || undefined,
        department:      fields.department     || undefined,
        all_control_ids: controlIds.length ? controlIds : undefined,
        coso_components: overview.coso_components?.length ? overview.coso_components : undefined,
        financial_impact:      overview.financial_impact                      || undefined,
        existing_summary:      stripHtml(overview.summary_of_deficiency)      || undefined,
      };
      const res = await axiosInstance.post('/api/issues/ai-generate-summary', body);
      const generated = res.data.summary_of_deficiency;
      if (!generated) throw new Error('No summary returned from AI');
      setOverview(prev => {
        const next = { ...prev, summary_of_deficiency: toRichContent(generated) };
        saveOverview(next);
        return next;
      });
      setValidationErrors(v => ({ ...v, summary_of_deficiency: false }));
      if (res.data.confidence_warning) { setAiWarning(res.data.confidence_warning); setTimeout(() => setAiWarning(''), 8000); }
    } catch (e) {
      console.error('[AI Generate Summary]', e);
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : (detail || e?.message || 'AI generation failed. Try again.');
      setAiWarning(resolve403ErrorMessage(e, msg));
      setTimeout(() => setAiWarning(''), 8000);
    }
    finally { setSummaryGenerating(false); }
  };

  // ── AI Suggest Root Cause ────────────────────────────────────────────────────

  const handleSuggestRootCause = async () => {
    setRootCauseLoading(true);
    setAiWarning('');
    try {
      const body = {
        issue_id:              id,
        summary_of_deficiency: stripHtml(overview.summary_of_deficiency || issue?.summary_of_deficiency || ''),
        classification:        fields.classification  || undefined,
        category:              fields.category        || undefined,
        coso_components:       overview.coso_components?.length ? overview.coso_components : undefined,
        financial_impact:      overview.financial_impact || undefined,
      };
      const res = await axiosInstance.post('/api/issues/ai-suggest-root-cause', body);
      const generated = res.data.root_cause_analysis;
      if (!generated) throw new Error('No root cause returned from AI');
      setOverview(prev => {
        const next = { ...prev, root_cause_analysis: toRichContent(generated) };
        saveOverview(next);
        return next;
      });
      setValidationErrors(v => ({ ...v, root_cause_analysis: false }));
      if (res.data.confidence_warning) { setAiWarning(res.data.confidence_warning); setTimeout(() => setAiWarning(''), 8000); }
    } catch (e) {
      console.error('[AI Suggest Root Cause]', e);
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : (detail || e?.message || 'AI generation failed. Try again.');
      setAiWarning(resolve403ErrorMessage(e, msg));
      setTimeout(() => setAiWarning(''), 8000);
    }
    finally { setRootCauseLoading(false); }
  };

  // ── AI Suggest Remediation ───────────────────────────────────────────────────

  const handleSuggestRemediation = async () => {
    setRemediationGenerating(true);
    try {
      const body = {
        issue_id:               id,
        summary_of_deficiency:  stripHtml(overview.summary_of_deficiency || issue?.summary_of_deficiency || ''),
        classification:         fields.classification || undefined,
        existing_action_plans:  actionPlans.length ? actionPlans.map(ap => ({
          title:       ap.title,
          description: ap.description,
          status:      ap.status,
        })) : undefined,
      };
      const res = await axiosInstance.post('/api/issues/ai-suggest-remediation', body);
      const next = { ...remediationData, plan: toRichContent(res.data.recommended_action) };
      setRemediationData(next);
      saveRemediationData(next);
      if (res.data.confidence_warning) { setAiWarning(res.data.confidence_warning); setTimeout(() => setAiWarning(''), 6000); }
    } catch (e) { console.error('[AI Suggest Remediation]', e); }
    finally { setRemediationGenerating(false); }
  };

  // ── Send to Stakeholders ─────────────────────────────────────────────────────

  const addSendEmail = (val) => {
    const email = val.trim().replace(/,+$/, '');
    if (!email || sendEmails.includes(email)) { setSendEmailInput(''); return; }
    setSendEmails(prev => [...prev, email]);
    setSendEmailInput('');
  };

  const handleSend = async () => {
    if (sendEmails.length === 0) { setSendError('Add at least one recipient.'); return; }
    setSending(true); setSendError('');
    try {
      await axiosInstance.post(`/api/sox-issues/${id}/notify`, {
        recipients: sendEmails,
        note:       sendNote,
      });
      setShowSendModal(false);
      setSendEmails([]); setSendNote(''); setSendEmailInput('');
      showSaved();
    } catch (e) {
      setSendError(resolve403ErrorMessage(e, e.response?.data?.error || 'Failed to send notification.'));
    } finally { setSending(false); }
  };

  // ── Status change ───────────────────────────────────────────────────────────

  const handleStatusChange = async (newStatus) => {
    setShowStatusMenu(false);
    await handleIssueAdvance(newStatus);
  };

  const toggleRecurring = () => {
    const next = !fields.is_repeat;
    setFields(f => ({ ...f, is_repeat: next }));
    setIssue(prev => ({ ...prev, is_repeat: next }));
    saveFields({ is_repeat: next });
  };

  const fetchAvailableRuns = async (controls) => {
    await Promise.allSettled(
      controls.map(async (c) => {
        const controlUuid = resolveControlUuid(c, c.sox_control_id);
        if (!controlUuid || !isControlUuid(controlUuid)) return;
        try {
          const issueYearId = issue?.audit_year_id || selectedAuditYearId;
          const res = await axiosInstance.get(`/api/control-test-runs/control/${controlUuid}`, {
            params: issueYearId ? { audit_year_id: issueYearId } : undefined,
          });
          setAvailableRunsByControl(prev => ({ ...prev, [c.sox_control_id]: res.data.runs || [] }));
        } catch {
          setAvailableRunsByControl(prev => ({ ...prev, [c.sox_control_id]: prev[c.sox_control_id] || [] }));
        }
      })
    );
  };

  const toggleTestRun = (c, run) => {
    const controlKey = c.control_id || c.sox_control_id;
    const runNum = run.run_number;
    const existing = overview.linked_test_runs || [];
    const isSelected = existing.some(r => r.control_id === controlKey && r.run_number === runNum);
    const entry = {
      control_id:       controlKey,
      run_number:       runNum,
      test_run_id:      run.id ?? null,
      overall_status:   run.execution?.overall_status ?? null,
      total_exceptions: run.execution?.total_exceptions ?? null,
      total_population: run.total_population ?? null,
    };
    const next = isSelected
      ? existing.filter(r => !(r.control_id === controlKey && r.run_number === runNum))
      : [...existing, entry];
    const nextOverview = { ...overview, linked_test_runs: next };
    setOverview(nextOverview);
    saveOverview(nextOverview);
  };

  const handleSetControls = async (controls) => {
    setSavingControls(true);
    try {
      const sox_control_ids = controls.map(c => c.sox_control_id);
      await axiosInstance.put(`/api/sox-issues/${id}/controls`, { sox_control_ids });
      setLinkedControls(controls);
      setShowControlsSelect(false);
      fetchAvailableRuns(controls);
    } catch (e) { console.error('[SetControls]', e); }
    finally { setSavingControls(false); }
  };

  const handleAutoDetectRecurring = async () => {
    const controlIds = linkedControls.map(c => c.sox_control_id).filter(Boolean);
    if (!controlIds.length) return;
    try {
      const issueScopeYearId = issue?.audit_year_id || selectedAuditYearId;
      const issuesParams = issueScopeYearId ? { audit_year_id: issueScopeYearId } : {};
      const res = await axiosInstance.get('/api/sox-issues', { params: issuesParams });
      const others = (res.data.issues || []).filter(iss => {
        if (iss.id === id) return false;
        return (iss.controls || []).some(c => controlIds.includes(c.sox_control_id));
      });
      setPriorIssues(others);
      if (others.length > 0 && !fields.is_repeat) {
        setFields(f => ({ ...f, is_repeat: true }));
        saveFields({ is_repeat: true });
      }
    } catch (e) { console.error('[AutoDetectRecurring]', e); }
  };

  // Run auto-detect and prefetch available test runs when controls are loaded/changed
  useEffect(() => {
    if (linkedControls.length > 0) {
      handleAutoDetectRecurring();
      fetchAvailableRuns(linkedControls);
    }
  }, [linkedControls.length]);

  // Close controls dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (controlsDropdownRef.current && !controlsDropdownRef.current.contains(e.target)) {
        setShowControlsSelect(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Management Review tab only appears when management is actually reviewing
  const activeSubTabs = useMemo(() => {
    if (issue?.status === 'reopened' && activeStep === 4) return SUB_TABS_REOPENED;
    const base = SUB_TABS_BY_STEP[activeStep] || [];
    if (activeStep === 1 && !['in_management_review', 'in_discussion'].includes(issue?.status)) {
      return base.filter(t => t !== 'Management Review');
    }
    return base;
  }, [activeStep, issue?.status]);

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
    </div>
  );

  if (!issue) return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-center">
      <AlertTriangle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
      <p className="text-gray-500">Issue not found.</p>
      <button onClick={() => navigate('/issues')} className="mt-4 text-sm text-blue-600 hover:underline">← Back to Issues</button>
    </div>
  );

  const isClosed     = issue.status === 'closed';
  const isReopened   = issue.status === 'reopened';
  const severityCfg  = SEVERITY_CONFIG[fields.classification];
  const statusEntry  = STATUS_DISPLAY[issue.status] || { label: issue.status, icon: Clock };
  const StatusIcon   = statusEntry.icon;
  const statusColor  = STATUS_COLORS[issue.status] || 'text-gray-600';

  const getMandatoryErrors = () => {
    const errs = {};
    if (!fields.classification)                  errs.classification = true;
    if (!fields.department)                      errs.department = true;
    if (!fields.category)                        errs.category = true;
    if (!fields.assignee_name?.trim())           errs.assignee_name = true;
    if (!fields.period)                          errs.period = true;
    if (!overview.coso_components?.length)                   errs.coso_components = true;
    if (!stripHtml(overview.summary_of_deficiency)?.replace(/ /g, ' ').trim()) errs.summary_of_deficiency = true;
    if (!stripHtml(overview.root_cause_analysis)?.replace(/ /g, ' ').trim())   errs.root_cause_analysis = true;
    if (!stripHtml(overview.impact)?.replace(/ /g, ' ').trim())                errs.impact = true;
    return errs;
  };

  // Recomputed every render — drives the disabled state of "Mark as Reviewed"
  const mandatoryErrors = getMandatoryErrors();
  const canMarkReviewed = Object.keys(mandatoryErrors).length === 0;

  const handleMarkReviewed = () => {
    if (!canMarkReviewed) {
      setValidationErrors(mandatoryErrors);
      return;
    }
    setValidationErrors({});
    handleIssueAdvance('reviewed');
  };

  const fieldCls = (key) => validationErrors[key]
    ? 'ring-2 ring-red-400 rounded'
    : '';

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Hidden file inputs — globally available */}
      <input
        ref={evidenceFileRef}
        type="file"
        className="hidden"
        onChange={e => {
          handleEvidenceUpload(e.target.files?.[0], apEvidenceRef);
          e.target.value = '';
        }}
      />
      <input
        type="file"
        ref={memoFileRef}
        accept=".pdf,.docx"
        className="hidden"
        onChange={e => { handleMemoFileSelect(e.target.files[0]); e.target.value = ''; }}
      />

      {/* ── Sticky Header ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => navigate('/issues')} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <span className="text-sm font-mono font-semibold text-blue-600 shrink-0">{issue.issue_id}</span>
              {severityCfg && (
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 ${severityCfg.bg} ${severityCfg.text}`}>
                  {severityCfg.short}
                </span>
              )}
              {editingTitle ? (
                <input
                  autoFocus
                  value={titleValue}
                  onChange={e => setTitleValue(e.target.value)}
                  onBlur={handleTitleBlur}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setTitleValue(issue?.title || ''); setEditingTitle(false); } }}
                  className="text-base font-semibold text-gray-900 border-b border-indigo-400 outline-none bg-transparent min-w-[200px] max-w-sm"
                />
              ) : (
                <h1
                  className={`text-base font-semibold text-gray-900 truncate ${isClosed ? 'cursor-default' : 'cursor-pointer hover:text-indigo-700'} transition-colors`}
                  title={isClosed ? undefined : 'Click to edit title'}
                  onClick={() => { if (!isClosed) setEditingTitle(true); }}
                >
                  {issue.title || <span className="text-gray-400 italic font-normal">Untitled issue</span>}
                </h1>
              )}
            </div>
            <div className="flex items-center gap-4 shrink-0">
              {saving && <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />}
              {saveMsg && !saving && (
                <span className="flex items-center gap-1 text-xs text-green-600"><Save className="h-3.5 w-3.5" />{saveMsg}</span>
              )}
              {aiWarning && (
                <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg max-w-xs">{aiWarning}</span>
              )}
              {/* Clone button — hidden for closed issues */}
              {!isClosed && (
                <button
                  onClick={() => setShowCloneConfirm(true)}
                  disabled={cloningIssue}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Clone
                </button>
              )}
              {/* Memo button — opens slide-in panel */}
              <button
                onClick={() => setShowMemoPanel(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Memo
              </button>
              {/* Evidence button — opens slide-in panel */}
              <button
                onClick={() => setShowEvidencePanel(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
              >
                <FileText className="h-3.5 w-3.5" />Evidence
              </button>
              {/* Re-open button — only when closed */}
              {isClosed && (
                <button
                  onClick={() => { setReopenReason(''); setReopenModal(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 border border-amber-300 bg-amber-50 rounded-full hover:bg-amber-100 transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" />Re-open
                </button>
              )}
              {/* Escalated badge (overlay — shown when is_escalated flag set or status=escalated) */}
              {(issue.is_escalated || issue.status === 'escalated') && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                  <Zap className="h-3.5 w-3.5" />Escalated
                </span>
              )}
              {/* Escalation toggle (available during remediation states) */}
              {['plan_accepted','remediation_in_progress','remediation_complete'].includes(issue.status) && !issue.is_escalated && (
                <button
                  onClick={async () => {
                    try {
                      await axiosInstance.put(`/api/sox-issues/${id}`, { is_escalated: true });
                      setIssue(prev => ({ ...prev, is_escalated: true }));
                    } catch (e) { console.error(e); }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-purple-600 border border-purple-200 hover:bg-purple-50 transition-colors"
                >
                  <Zap className="h-3.5 w-3.5" />Escalate
                </button>
              )}
            </div>
          </div>

          {/* Stage Pipeline Stepper — clickable */}
          {(() => {
            const currentIdx = STATUS_TO_STAGE[issue.status] ?? 0;
            // verification_complete / verification_failed: fill through Verification, highlight Closed
            // closed: fill all steps
            const displayIdx = issue.status === 'closed' ? 5
              : (issue.status === 'verification_complete' || issue.status === 'verification_failed') ? 4
              : currentIdx;
            return (
              <div className="flex items-center gap-0 mt-5 mb-1 overflow-x-auto">
                {STAGE_PIPELINE.map((stage, idx) => {
                  const isDone    = idx < displayIdx;
                  const isCurrent = idx === displayIdx;
                  const isViewing = idx === activeStep;
                  return (
                    <React.Fragment key={stage.key}>
                      <div
                        className="flex flex-col items-center min-w-[80px] cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleStepClick(idx)}
                      >
                        <div className={`flex items-center justify-center w-7 h-7 rounded-full border-2 transition-colors ${
                          isDone    ? 'bg-blue-600 border-blue-600 text-white' :
                          isCurrent ? 'bg-white border-blue-600 text-blue-600' :
                                      'bg-white border-gray-200 text-gray-300'
                        } ${isViewing && !isCurrent ? 'ring-2 ring-offset-1 ring-blue-300' : ''}`}>
                          {isDone
                            ? <CheckCircle className="h-4 w-4" />
                            : <stage.icon className="h-3.5 w-3.5" />
                          }
                        </div>
                        <span className={`mt-1 text-xs font-medium whitespace-nowrap ${
                          isViewing ? 'text-blue-600' : isCurrent ? 'text-blue-500' : isDone ? 'text-gray-500' : 'text-gray-300'
                        } ${isViewing && !isCurrent ? 'underline' : ''}`}>{stage.shortLabel}</span>
                      </div>
                      {idx < STAGE_PIPELINE.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1 min-w-[16px] ${idx < currentIdx ? 'bg-blue-600' : 'bg-gray-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()}

          {/* Mini phase indicator — Review step only */}
          {activeStep === 1 && issue && (() => {
            const s = issue.status;
            const phase1Done   = ['in_management_review','in_discussion'].includes(s) || STATUS_TO_STAGE[s] > 1;
            const phase1Rework = s === 'rework';
            const phase2Active = ['in_management_review','in_discussion'].includes(s);
            const phase2Rework = s === 'rework';
            const phase2Done   = STATUS_TO_STAGE[s] > 1;
            return (
              <div className="flex items-center gap-2 mt-3 mb-1">
                <span className="text-xs text-gray-400 font-medium mr-1">Review phase:</span>
                <button
                  onClick={() => setActiveSubTab('Issue Details')}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    phase1Rework ? 'bg-orange-50 border-orange-200 text-orange-600' :
                    phase1Done   ? 'bg-blue-50 border-blue-200 text-blue-600' :
                                   'bg-blue-600 border-blue-600 text-white'
                  }`}
                >
                  {phase1Rework
                    ? <RefreshCw className="h-3 w-3" />
                    : phase1Done
                      ? <CheckCircle className="h-3 w-3" />
                      : <CircleDot className="h-3 w-3" />
                  }
                  Team Review
                </button>
                <ChevronRight className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                <button
                  onClick={() => setActiveSubTab('Management Review')}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    phase2Rework ? 'bg-orange-50 border-orange-200 text-orange-600' :
                    phase2Done   ? 'bg-blue-50 border-blue-200 text-blue-600' :
                    phase2Active ? 'bg-blue-600 border-blue-600 text-white' :
                                   'bg-white border-gray-200 text-gray-400'
                  }`}
                >
                  {phase2Rework
                    ? <RefreshCw className="h-3 w-3" />
                    : phase2Done
                      ? <CheckCircle className="h-3 w-3" />
                      : phase2Active
                        ? <CircleDot className="h-3 w-3" />
                        : <Circle className="h-3 w-3" />
                  }
                  {phase2Rework ? 'Rework Requested' : 'Management Review'}
                </button>
              </div>
            );
          })()}

          {/* Sub-tabs (only for steps that have them) */}
          {activeSubTabs.length > 0 && (
            <div className="flex gap-0 mt-4 -mb-px">
              {activeSubTabs.map(subtab => (
                <button
                  key={subtab}
                  onClick={() => setActiveSubTab(subtab)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeSubTab === subtab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {subtab}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className={`max-w-7xl mx-auto px-6 py-6${isClosed ? ' pointer-events-none opacity-90 select-none' : ''}`}>

        {/* History banner — shown when viewing a completed past step */}
        {activeStep < (STATUS_TO_STAGE[issue.status] ?? 0) && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-sm text-blue-600 mb-6">
            <CheckCircle className="h-4 w-4 shrink-0 text-blue-400" />
            Viewing history — this step is complete.
          </div>
        )}


        {/* ── ISSUE DETAILS (all steps) ── */}
        {activeSubTab === 'Issue Details' && (
          <div className="space-y-6">

            {/* Metadata card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              {/* 3-col grid */}
              <div className="grid grid-cols-3 gap-x-12 gap-y-6">

                {/* Status */}
                <div>
                  <MetaLabel>Status</MetaLabel>
                  <div className={`flex items-center gap-2 text-base font-bold ${statusColor}`}>
                    <StatusIcon className="h-5 w-5" />
                    {statusEntry?.label || issue.status}
                  </div>
                </div>

                {/* Classification */}
                <div className={fieldCls('classification')}>
                  <MetaLabel>Classification *</MetaLabel>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-gray-400 shrink-0" />
                    {severityCfg && (
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 ${severityCfg.bg} ${severityCfg.text}`}>
                        {severityCfg.short}
                      </span>
                    )}
                    <div className="relative">
                      <select
                        value={fields.classification}
                        onChange={e => { setFields(f => ({ ...f, classification: e.target.value })); setValidationErrors(v => ({ ...v, classification: false })); }}
                        onBlur={() => handleFieldBlur('classification')}
                        className="appearance-none bg-transparent text-sm font-medium text-gray-800 pr-5 focus:outline-none cursor-pointer"
                      >
                        {CLASSIFICATION_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Issue Type */}
                <div>
                  <MetaLabel>Issue Type *</MetaLabel>
                  <div className="flex gap-1.5 mt-1">
                    {['operating_effectiveness', 'design'].map(t => (
                      <button key={t} type="button"
                        onClick={() => { setFields(f => ({ ...f, issue_type: t })); saveFields({ issue_type: t }); }}
                        className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${fields.issue_type === t ? (t === 'design' ? 'bg-purple-600 border-purple-600 text-white' : 'bg-blue-600 border-blue-600 text-white') : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                        {t === 'operating_effectiveness' ? 'Operating Effectiveness' : 'Design Deficiency'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Department */}
                <div className={fieldCls('department')}>
                  <MetaLabel>Department *</MetaLabel>
                  <div className="relative inline-block">
                    <select
                      value={fields.department}
                      onChange={e => { setFields(f => ({ ...f, department: e.target.value })); setValidationErrors(v => ({ ...v, department: false })); }}
                      onBlur={() => handleFieldBlur('department')}
                      className="appearance-none pl-3 pr-7 py-1 text-sm font-semibold text-gray-800 border border-gray-200 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    >
                      <option value="">Select...</option>
                      {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Category */}
                <div className={fieldCls('category')}>
                  <MetaLabel>Category *</MetaLabel>
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-4 w-4 text-gray-400 shrink-0" />
                    {addingCategory ? (
                      <div className="flex items-center gap-1">
                        <input
                          autoFocus
                          value={newCategoryInput}
                          onChange={e => setNewCategoryInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && newCategoryInput.trim()) {
                              const trimmed = newCategoryInput.trim();
                              const all = [...BASE_CATEGORIES, ...customCategories];
                              if (!all.includes(trimmed)) {
                                const next = [...customCategories, trimmed];
                                setCustomCategories(next);
                                localStorage.setItem(CUSTOM_CATEGORIES_KEY, JSON.stringify(next));
                              }
                              setFields(f => ({ ...f, category: trimmed }));
                              setValidationErrors(v => ({ ...v, category: false }));
                              handleFieldBlur('category');
                              setNewCategoryInput('');
                              setAddingCategory(false);
                            }
                            if (e.key === 'Escape') { setNewCategoryInput(''); setAddingCategory(false); }
                          }}
                          placeholder="New category name…"
                          className="text-sm border-b border-blue-400 bg-transparent focus:outline-none w-36"
                        />
                        <button onClick={() => { setNewCategoryInput(''); setAddingCategory(false); }} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                      </div>
                    ) : (
                      <div className="relative">
                        <select
                          value={fields.category}
                          onChange={e => {
                            if (e.target.value === '__add__') { setAddingCategory(true); return; }
                            setFields(f => ({ ...f, category: e.target.value }));
                            setValidationErrors(v => ({ ...v, category: false }));
                          }}
                          onBlur={() => { if (fields.category) handleFieldBlur('category'); }}
                          className="appearance-none bg-transparent text-sm font-semibold text-gray-800 pr-5 focus:outline-none cursor-pointer"
                        >
                          <option value="">Select...</option>
                          {[...BASE_CATEGORIES, ...customCategories].map(c => <option key={c} value={c}>{c}</option>)}
                          <option value="__add__">+ Add category…</option>
                        </select>
                        <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Assignee */}
                <div className={fieldCls('assignee_name')}>
                  <MetaLabel>Assignee *</MetaLabel>
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <input
                        type="text"
                        value={fields.assignee_name}
                        onChange={e => { setFields(f => ({ ...f, assignee_name: e.target.value })); setValidationErrors(v => ({ ...v, assignee_name: false })); }}
                        onBlur={() => handleFieldBlur('assignee_name')}
                        placeholder="Assignee name"
                        className="block text-sm font-semibold text-gray-800 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-400 focus:outline-none w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Period */}
                <div className={fieldCls('period')}>
                  <MetaLabel>Period *</MetaLabel>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
                    <div className="relative">
                      <select
                        value={fields.period}
                        onChange={e => { setFields(f => ({ ...f, period: e.target.value })); setValidationErrors(v => ({ ...v, period: false })); }}
                        onBlur={() => handleFieldBlur('period')}
                        className="appearance-none bg-transparent text-sm font-semibold text-gray-800 pr-5 focus:outline-none cursor-pointer"
                      >
                        <option value="">Select...</option>
                        {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Recurring Issue */}
                <div>
                  <MetaLabel>Recurring Issue</MetaLabel>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleRecurring}
                      className={`text-sm font-semibold transition-colors ${fields.is_repeat ? 'text-amber-600 hover:text-amber-800' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      {fields.is_repeat ? 'Yes' : 'No'}
                    </button>
                    {priorIssues.length > 0 && (
                      <button
                        onClick={() => setShowPriorIssues(s => !s)}
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        {priorIssues.length} prior {priorIssues.length === 1 ? 'issue' : 'issues'} {showPriorIssues ? '▲' : '▼'}
                      </button>
                    )}
                  </div>
                  {showPriorIssues && priorIssues.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {priorIssues.map(iss => (
                        <div key={iss.id} className="flex items-center gap-2 text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                          <span className="font-mono font-semibold text-amber-700">{iss.issue_id}</span>
                          {iss.period && <span className="text-amber-600">{iss.period}</span>}
                          {iss.title && <span className="truncate text-gray-500">{iss.title}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Related Issues — linked chips + add dropdown */}
                <div className="col-span-3">
                  <MetaLabel>Related Issues</MetaLabel>
                  <div className="flex flex-wrap items-center gap-2">
                    {sourceData.related_issue_ids.map((iid, i) => {
                      const linked = allOrgIssues.find(iss => iss.issue_id === iid);
                      return (
                        <span key={i} className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full border border-gray-200 max-w-xs">
                          <span className="font-mono font-semibold">{iid}</span>
                          {linked?.title && <span className="text-gray-500 truncate">{linked.title}</span>}
                          <button
                            onClick={() => {
                              const next = { ...sourceData, related_issue_ids: sourceData.related_issue_ids.filter((_, j) => j !== i) };
                              setSourceData(next);
                              saveSourceData(next);
                            }}
                            className="text-gray-400 hover:text-gray-700 shrink-0"
                          >×</button>
                        </span>
                      );
                    })}
                    <div className="relative">
                      <select
                        value=""
                        onChange={e => {
                          if (!e.target.value) return;
                          if (!sourceData.related_issue_ids.includes(e.target.value)) {
                            const next = { ...sourceData, related_issue_ids: [...sourceData.related_issue_ids, e.target.value] };
                            setSourceData(next);
                            saveSourceData(next);
                          }
                        }}
                        className="appearance-none pl-2 pr-7 py-1 text-xs border border-dashed border-gray-300 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-500 cursor-pointer"
                      >
                        <option value="">+ Link issue…</option>
                        {allOrgIssues
                          .filter(iss => !sourceData.related_issue_ids.includes(iss.issue_id))
                          .map(iss => (
                            <option key={iss.id} value={iss.issue_id}>
                              {iss.issue_id}{iss.title ? ` — ${iss.title}` : ''}
                            </option>
                          ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Controls & Test Runs */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-gray-700">Controls & Test Runs</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Link controls and select relevant test runs for this issue.</p>
                </div>
                <div className="relative" ref={controlsDropdownRef}>
                  <button
                    onClick={() => {
                      if (allControls.length === 0) {
                        axiosInstance.get('/api/sox-controls').then(res => setAllControls(res.data.controls || res.data || [])).catch(() => {});
                      }
                      if (!showControlsSelect) fetchAvailableRuns(linkedControls);
                      setShowControlsSelect(s => !s);
                    }}
                    className="flex items-center gap-1.5 text-xs text-indigo-600 border border-indigo-200 px-3 py-1.5 rounded-full hover:bg-indigo-50 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {linkedControls.length > 0 ? 'Edit controls & runs' : 'Link controls'}
                  </button>
                  {showControlsSelect && (
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg w-96 max-h-[500px] overflow-y-auto">
                      {/* Controls section */}
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 sticky top-0">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Controls</span>
                      </div>
                      {allControls.length === 0 && (
                        <p className="px-4 py-3 text-sm text-gray-400">Loading controls…</p>
                      )}
                      {allControls.map(ctrl => {
                        const already = linkedControls.some(c => c.sox_control_id === ctrl.id);
                        return (
                          <button
                            key={ctrl.id}
                            onClick={() => {
                              const next = already
                                ? linkedControls.filter(c => c.sox_control_id !== ctrl.id)
                                : [...linkedControls, { sox_control_id: ctrl.id, control_id: ctrl.control_id || ctrl.id }];
                              handleSetControls(next);
                            }}
                            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${already ? 'font-semibold text-indigo-700' : 'text-gray-700'}`}
                          >
                            <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${already ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300'}`}>
                              {already && <span className="text-xs font-bold">✓</span>}
                            </span>
                            <span className="font-mono text-xs text-gray-500 shrink-0">{ctrl.control_id || ctrl.id}</span>
                            <span className="truncate">{ctrl.title || ctrl.name || ''}</span>
                          </button>
                        );
                      })}
                      {/* Test runs section per linked control */}
                      {linkedControls.length > 0 && (
                        <>
                          <div className="px-4 py-2 bg-gray-50 border-t border-b border-gray-100 sticky top-8">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Test Runs</span>
                          </div>
                          {linkedControls.map(c => {
                            const controlKey = c.control_id || c.sox_control_id;
                            const allRuns = availableRunsByControl[c.sox_control_id];
                            // The newest finished attempt of each rerun chain (as when creating an issue),
                            // plus any run already linked so it can still be unlinked here.
                            const linkedNums = new Set((overview.linked_test_runs || [])
                              .filter(r => r.control_id === controlKey).map(r => r.run_number));
                            const runs = allRuns === undefined ? undefined : (() => {
                              const offered = pickIssueRuns(allRuns);
                              const offeredIds = new Set(offered.map(r => r.id));
                              const stillLinked = allRuns.filter(r => linkedNums.has(r.run_number) && !offeredIds.has(r.id));
                              return [...offered, ...stillLinked].sort((a, b) => (b.run_number ?? 0) - (a.run_number ?? 0));
                            })();
                            return (
                              <div key={c.sox_control_id} className="px-4 py-3 border-b border-gray-50 last:border-0">
                                <p className="text-xs font-semibold text-indigo-700 mb-2">{controlKey}</p>
                                {runs === undefined ? (
                                  <div className="flex items-center gap-1 text-xs text-gray-400"><Loader2 className="h-3 w-3 animate-spin" />Loading…</div>
                                ) : runs.length === 0 ? (
                                  <p className="text-xs text-gray-400 italic">No test runs available.</p>
                                ) : (
                                  <div className="flex flex-col gap-1">
                                    {runs.map(run => {
                                      const runNum = run.run_number;
                                      const execStatus = run.execution?.overall_status;
                                      const execExceptions = run.execution?.total_exceptions;
                                      const isSelected = (overview.linked_test_runs || []).some(r => r.control_id === controlKey && r.run_number === runNum);
                                      return (
                                        <button
                                          key={runNum}
                                          onClick={() => toggleTestRun(c, run)}
                                          className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border transition-colors text-left ${isSelected ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                        >
                                          <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300'}`}>
                                            {isSelected && <span className="text-[8px] font-bold leading-none">✓</span>}
                                          </span>
                                          <span className="font-semibold">Run {runNum}</span>
                                          {execExceptions != null && <span className="opacity-60">· {execExceptions} exc</span>}
                                          {execStatus && (
                                            <span className={`capitalize ${execStatus === 'failed' ? 'text-red-600' : execStatus === 'warning' ? 'text-amber-600' : 'text-gray-500'}`}>{execStatus}</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {linkedControls.length === 0 ? (
                <p className="text-sm text-gray-400">No controls linked yet. Click "Link controls" to associate a control.</p>
              ) : (
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                  {/* Header row */}
                  <div className="grid grid-cols-[180px_1fr] bg-gray-50 px-4 py-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Control</span>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Linked Test Runs</span>
                  </div>
                  {linkedControls.map(c => {
                    const controlKey = c.control_id || c.sox_control_id;
                    const linkedRuns = (overview.linked_test_runs || []).filter(r => r.control_id === controlKey);
                    return (
                      <div key={c.sox_control_id} className="grid grid-cols-[180px_1fr] items-center px-4 py-3 gap-4">
                        {/* Control chip + remove */}
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 truncate">
                            <Shield className="h-3 w-3 text-indigo-400 shrink-0" />
                            <span className="truncate">{controlKey}</span>
                          </span>
                          <button
                            onClick={() => handleSetControls(linkedControls.filter(x => x.sox_control_id !== c.sox_control_id))}
                            className="text-gray-300 hover:text-red-500 transition-colors shrink-0 text-base leading-none"
                            title="Remove control"
                          >×</button>
                          {savingControls && <Loader2 className="h-3 w-3 text-gray-400 animate-spin shrink-0" />}
                        </div>
                        {/* Only show linked test runs */}
                        {linkedRuns.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">No test runs linked. Use "Edit controls & runs" to link one.</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {linkedRuns.map(run => {
                              const statusColor = run.overall_status === 'failed'
                                ? 'bg-red-100 border-red-300 text-red-700'
                                : run.overall_status === 'warning'
                                ? 'bg-amber-100 border-amber-300 text-amber-700'
                                : 'bg-blue-100 border-blue-300 text-blue-700';
                              return (
                                <span key={run.run_number} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border ${statusColor}`}>
                                  <CheckCircle className="h-3 w-3 shrink-0" />
                                  <span className="font-semibold">Run {run.run_number}</span>
                                  {run.total_exceptions != null && <span className="opacity-60">· {run.total_exceptions} exc</span>}
                                  {run.overall_status && <span className="opacity-60 capitalize">{run.overall_status}</span>}
                                  <button
                                    onClick={() => {
                                      const next = (overview.linked_test_runs || []).filter(r => !(r.control_id === controlKey && r.run_number === run.run_number));
                                      const nextOverview = { ...overview, linked_test_runs: next };
                                      setOverview(nextOverview);
                                      saveOverview(nextOverview);
                                    }}
                                    className="ml-0.5 opacity-50 hover:opacity-100 hover:text-red-600 transition-opacity"
                                    title="Remove this test run"
                                  >×</button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* COSO chips */}
            <div className={`bg-white rounded-xl border p-6 ${validationErrors.coso_components ? 'border-red-400' : 'border-gray-200'}`}>
              <h2 className="text-sm font-semibold text-gray-700 mb-1">COSO Framework Components *</h2>
              <p className="text-xs text-gray-400 mb-4">Select the COSO components impacted by this issue.</p>
              <div className="flex flex-wrap gap-2">
                {COSO_COMPONENTS.map(component => {
                  const active = overview.coso_components.includes(component);
                  return (
                    <button
                      key={component}
                      onClick={() => toggleCoso(component)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                        active ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                      }`}
                    >
                      {component}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Validation banner */}
            {Object.values(validationErrors).some(Boolean) && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-700">Required fields missing</p>
                  <p className="text-xs text-red-600 mt-0.5">Fill in all required fields (marked with *) before marking as Reviewed.</p>
                </div>
              </div>
            )}

            {/* Summary of Deficiency */}
            <div className={`bg-white rounded-xl border p-6 ${validationErrors.summary_of_deficiency ? 'border-red-400' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-800">Summary of Deficiency *</h2>
                <button
                  type="button"
                  onClick={() => toggleLock('summary')}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors ${isLocked('summary') ? 'text-red-500 border-red-200 bg-red-50' : 'text-gray-400 border-gray-200 hover:text-blue-500 hover:border-blue-200'}`}
                >
                  {isLocked('summary') ? <><Lock className="h-3 w-3" />Locked</> : <><Unlock className="h-3 w-3" />Editable</>}
                </button>
              </div>
              <RichTextEditor
                minRows={4}
                readOnly={isLocked('summary')}
                value={overview.summary_of_deficiency}
                onChange={html => { setOverview(o => ({ ...o, summary_of_deficiency: html })); setValidationErrors(v => ({ ...v, summary_of_deficiency: false })); }}
                onBlur={handleOverviewBlur}
                placeholder="Describe the control deficiency observed..."
              />
              <button
                type="button"
                onClick={handleGenerateSummary}
                disabled={summaryGenerating || isLocked('summary')}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 disabled:opacity-50 transition-colors"
              >
                {summaryGenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {summaryGenerating ? 'Generating…' : 'AI Generate Summary'}
              </button>
            </div>

            {/* Root Cause Analysis */}
            <div className={`bg-white rounded-xl border p-6 ${validationErrors.root_cause_analysis ? 'border-red-400' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-800">Root Cause Analysis *</h2>
                <button
                  type="button"
                  onClick={() => toggleLock('root_cause')}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors ${isLocked('root_cause') ? 'text-red-500 border-red-200 bg-red-50' : 'text-gray-400 border-gray-200 hover:text-blue-500 hover:border-blue-200'}`}
                >
                  {isLocked('root_cause') ? <><Lock className="h-3 w-3" />Locked</> : <><Unlock className="h-3 w-3" />Editable</>}
                </button>
              </div>
              <RichTextEditor
                minRows={4}
                readOnly={isLocked('root_cause')}
                value={overview.root_cause_analysis}
                onChange={html => { setOverview(o => ({ ...o, root_cause_analysis: html })); setValidationErrors(v => ({ ...v, root_cause_analysis: false })); }}
                onBlur={handleOverviewBlur}
                placeholder="Describe the root cause of this deficiency..."
              />
              <button
                type="button"
                onClick={handleSuggestRootCause}
                disabled={rootCauseLoading || isLocked('root_cause')}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 disabled:opacity-50 transition-colors"
              >
                {rootCauseLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {rootCauseLoading ? 'Generating…' : 'AI Suggest Root Cause'}
              </button>
            </div>

            {/* Impact */}
            <div className={`bg-white rounded-xl border p-6 ${validationErrors.impact ? 'border-red-400' : 'border-gray-200'}`}>
              <h2 className="text-sm font-semibold text-gray-800 mb-3">Impact *</h2>
              <RichTextEditor
                minRows={3}
                value={overview.impact}
                onChange={html => { setOverview(o => ({ ...o, impact: html })); setValidationErrors(v => ({ ...v, impact: false })); }}
                onBlur={handleOverviewBlur}
                placeholder="Describe the operational impact of this deficiency..."
              />
            </div>

            {/* Financial Impact */}
            <div className={`bg-white rounded-xl border p-6 ${validationErrors.financial_impact ? 'border-red-400' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="h-4 w-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-gray-800">Financial Impact *</h2>
              </div>
              <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3">
                <input
                  type="text"
                  value={overview.financial_impact}
                  onChange={e => { setOverview(o => ({ ...o, financial_impact: e.target.value })); setValidationErrors(v => ({ ...v, financial_impact: false })); }}
                  onBlur={handleOverviewBlur}
                  placeholder="e.g. $1.2M inventory valuation gap"
                  className="w-full text-sm font-semibold text-gray-900 bg-transparent border-none focus:outline-none placeholder-gray-400"
                />
                <div>
                  <input
                    ref={financialFileRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={e => setFinancialFile(e.target.files?.[0] || null)}
                  />
                  {financialFile ? (
                    <div className="flex items-center gap-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 w-fit">
                      <FileSpreadsheet className="h-4 w-4 text-green-600 shrink-0" />
                      <span className="truncate max-w-xs">{financialFile.name}</span>
                      <button onClick={() => setFinancialFile(null)} className="text-gray-400 hover:text-red-500 ml-1">×</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => financialFileRef.current?.click()}
                      className="flex items-center gap-2 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors"
                    >
                      <FileSpreadsheet className="h-4 w-4 text-gray-500" />
                      Upload Impact Calculation (.xlsx)
                    </button>
                  )}
                </div>
              </div>
            </div>


          </div>
        )}

        {/* ── REMEDIATION PLAN (all steps) ── */}
        {activeSubTab === 'Remediation Plan' && (
          <div className="space-y-6">

            {/* Remediation Plan */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-blue-600" />
                  <h2 className="text-base font-bold text-gray-900">Remediation Plan</h2>
                </div>
                <button
                  type="button"
                  onClick={() => toggleLock('remediation_plan')}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border transition-colors ${isLocked('remediation_plan') ? 'text-red-500 border-red-200 bg-red-50' : 'text-green-600 border-green-200 hover:text-blue-500 hover:border-blue-200'}`}
                >
                  {isLocked('remediation_plan') ? <><Lock className="h-3 w-3" />Locked</> : <><Unlock className="h-3 w-3" />Editable</>}
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-5">You can edit the remediation plan.</p>

              {/* Recommended Action */}
              <div className="mb-5">
                <p className="text-xs text-gray-500 font-medium mb-1">Recommended Action</p>
                <RichTextEditor
                  minRows={3}
                  readOnly={isLocked('remediation_plan')}
                  value={remediationData.plan}
                  onChange={html => setRemediationData(r => ({ ...r, plan: html }))}
                  onBlur={() => saveRemediationData(remediationData)}
                  placeholder="Describe the recommended remediation action..."
                />
                <button
                  type="button"
                  onClick={handleSuggestRemediation}
                  disabled={remediationGenerating || isLocked('remediation_plan')}
                  className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 disabled:opacity-50 border border-gray-200 px-3 py-1.5 rounded-lg transition-colors"
                >
                  {remediationGenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                  {remediationGenerating ? 'Generating…' : 'AI Suggest Remediation'}
                </button>
              </div>

              {/* Target Completion + Owner */}
              <div className="grid grid-cols-2 gap-6 border-t border-gray-100 pt-5">
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">Target Completion</p>
                  {remediationData.target_date ? (
                    <p className="text-base font-bold text-gray-900">
                      {new Date(remediationData.target_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400">Not set</p>
                  )}
                  <input
                    type="date"
                    value={remediationData.target_date}
                    onChange={e => setRemediationData(r => ({ ...r, target_date: e.target.value }))}
                    onBlur={() => saveRemediationData(remediationData)}
                    className="mt-1 text-xs text-gray-400 bg-transparent focus:outline-none border-b border-transparent hover:border-gray-300 focus:border-blue-400 w-32"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">Remediation Owner</p>
                  <input
                    type="text"
                    value={remediationData.owner_name}
                    onChange={e => setRemediationData(r => ({ ...r, owner_name: e.target.value }))}
                    onBlur={() => saveRemediationData(remediationData)}
                    placeholder="Owner name"
                    className="block text-base font-bold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-400 focus:outline-none w-full"
                  />
                </div>
              </div>
            </div>

            {/* Action Plans */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <CheckCircle className="h-4 w-4 text-blue-600 shrink-0" />
                    <h2 className="text-base font-bold text-gray-900">
                      Action Plans{actionPlans.length > 0 ? ` (${actionPlans.length})` : ''}
                    </h2>
                    {isReopened && (
                      <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full">
                        <Lock className="h-3 w-3" />Read-only
                      </span>
                    )}
                  </div>
                  {/* Progress bar */}
                  {actionPlans.length > 0 && (() => {
                    const done = actionPlans.filter(p => p.status === 'completed').length;
                    const pct  = Math.round((done / actionPlans.length) * 100);
                    return (
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">{done} of {actionPlans.length} complete</span>
                      </div>
                    );
                  })()}
                </div>
                {!isReopened && (
                  <button
                    onClick={() => { setShowApForm(true); setApForm(EMPTY_AP_FORM); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shrink-0 ml-4"
                  >
                    <Plus className="h-3.5 w-3.5" />Add Action
                  </button>
                )}
              </div>

              {apLoading ? (
                <div className="flex items-center justify-center py-12 gap-2">
                  <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : actionPlans.length === 0 && !showApForm ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <CheckCircle className="h-8 w-8 text-gray-200 mb-2" />
                  <p className="text-sm text-gray-400">No action plans yet. Add one to track remediation steps.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 px-6">
                  {[...actionPlans]
                    .sort((a, b) => {
                      // Overdue plans float to top
                      const now = new Date();
                      const aOverdue = a.due_date && calendarDateToLocal(a.due_date) < now && a.status !== 'completed';
                      const bOverdue = b.due_date && calendarDateToLocal(b.due_date) < now && b.status !== 'completed';
                      if (aOverdue && !bOverdue) return -1;
                      if (!aOverdue && bOverdue) return 1;
                      return 0;
                    })
                    .map(ap => {
                    const cfg = AP_STATUS_CONFIG[ap.status] || AP_STATUS_CONFIG.not_started;
                    const ApStatusIcon = cfg.icon;
                    const parsed = parseApDescription(ap.description);
                    const isOverdue = ap.due_date && calendarDateToLocal(ap.due_date) < new Date() && ap.status !== 'completed';
                    return (
                      <div key={ap.id} className="py-5">
                        {/* Row 1: badges + delete */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-xs font-mono font-semibold text-gray-400">{ap.plan_id}</span>

                          {/* Status badge — display only, no dropdown */}
                          <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                            <ApStatusIcon className="h-3 w-3" />{cfg.label}
                          </span>

                          {isOverdue && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                              <AlertTriangle className="h-3 w-3" />Overdue
                            </span>
                          )}

                          {ap.ticket_reference && (
                            <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                              <ExternalLink className="h-3 w-3" />{ap.ticket_reference}
                            </span>
                          )}

                          {!isReopened && (
                            <div className="ml-auto flex items-center gap-1">
                              <button onClick={() => handleDeleteAp(ap.id)} disabled={deletingAp === ap.id}
                                className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                                {deletingAp === ap.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Title */}
                        <p className="text-sm font-bold text-gray-900 mb-3">{ap.title}</p>

                        {/* Owner + Due Date grid */}
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          {ap.owner_name && (
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Owner</p>
                              <div className="flex items-start gap-1.5">
                                <User className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                                <p className="text-sm font-semibold text-gray-900">{ap.owner_name}</p>
                              </div>
                            </div>
                          )}
                          {ap.due_date && (
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Due Date</p>
                              <div className="flex items-center gap-1.5">
                                <Calendar className={`h-3.5 w-3.5 shrink-0 ${isOverdue ? 'text-red-400' : 'text-gray-400'}`} />
                                <p className={`text-sm font-semibold ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                                  {formatCalendarDate(ap.due_date, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Corrective Action */}
                        {parsed && (
                          <div className="border-t border-gray-100 pt-3 mb-3">
                            <p className="text-xs text-gray-400 font-medium">Corrective Action</p>
                            <p className="text-sm text-gray-700">{parsed}</p>
                          </div>
                        )}

                        {/* Completion notes (read-only, shown when completed) */}
                        {ap.status === 'completed' && ap.completion_notes && (
                          <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5 mb-3">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs text-green-700 font-semibold mb-0.5">
                                Completed {ap.completed_at ? new Date(ap.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                              </p>
                              <p className="text-xs text-green-800">{ap.completion_notes}</p>
                            </div>
                          </div>
                        )}

                        {/* Status badge only — execution controls are on the Execution page */}
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${AP_STATUS_CONFIG[ap.status]?.bg || 'bg-gray-100'} ${AP_STATUS_CONFIG[ap.status]?.color || 'text-gray-500'}`}>
                            {React.createElement(AP_STATUS_CONFIG[ap.status]?.icon || Circle, { className: 'h-3 w-3' })}
                            {AP_STATUS_CONFIG[ap.status]?.label || ap.status}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add Action Plan inline form */}
              {showApForm && (
                <div className="border-t border-gray-100 px-6 py-5 bg-gray-50 rounded-b-xl">
                  <form onSubmit={handleCreateAp} className="space-y-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold text-gray-700">New Action Plan</p>
                      <button type="button" onClick={() => setShowApForm(false)} className="text-gray-400 hover:text-gray-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <input type="text" required placeholder="Title *" value={apForm.title}
                      onChange={e => setApForm(f => ({ ...f, title: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                    <textarea rows={2} placeholder="Corrective Action" value={apForm.corrective_action}
                      onChange={e => setApForm(f => ({ ...f, corrective_action: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none" />
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" placeholder="Owner name" value={apForm.owner_name} onChange={e => setApForm(f => ({ ...f, owner_name: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                      <input type="date" value={apForm.due_date} onChange={e => setApForm(f => ({ ...f, due_date: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700" />
                      <input type="text" placeholder="Ticket ref (e.g. JIRA-123)" value={apForm.ticket_reference} onChange={e => setApForm(f => ({ ...f, ticket_reference: e.target.value }))} className="col-span-2 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button type="button" onClick={() => setShowApForm(false)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit" disabled={apSubmitting} className="flex items-center gap-1.5 px-4 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        {apSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        {apSubmitting ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ── SHARED CTAs: Issue Details + Remediation Plan ── */}
        {(activeSubTab === 'Issue Details' || activeSubTab === 'Remediation Plan') && (
          <div className="flex items-center justify-between mt-2">
            {issue.status !== 'closed' && issue.status !== 'reopened' && issue.status !== 'draft' && issue.status !== 'rework' && (
              <button
                onClick={() => { setCloseReason(''); setCloseModal(true); }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <X className="h-3.5 w-3.5" />Close Issue
              </button>
            )}
            <div className="ml-auto flex items-center gap-3">
              {(issue.status === 'draft' || issue.status === 'rework') && (
                <button
                  onClick={handleMarkReviewed}
                  disabled={!canMarkReviewed}
                  title={!canMarkReviewed ? 'Fill in all required fields (marked *) first' : ''}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  <CheckCircle className="h-4 w-4" />
                  {issue.status === 'rework' ? 'Submit for Team Review' : 'Submit for Review'}
                </button>
              )}
              {issue.status === 'reviewed' && (
                <button
                  onClick={() => handleIssueAdvance('in_management_review')}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <Send className="h-4 w-4" />Send to Management
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── STEP 2: EXECUTION ── */}
        {activeStep === 2 && activeSubTab === 'Remediation Execution' && (
          <div className="space-y-6">

            {/* Plan accepted: show start CTA */}
            {issue.status === 'plan_accepted' && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-blue-800">Ready to begin execution</p>
                  <p className="text-xs text-blue-600 mt-0.5">Start tracking progress against your action plans.</p>
                </div>
                <button
                  onClick={async () => {
                    if (actionPlans.length === 0) {
                      await handleIssueAdvance('remediation_in_progress');
                      await handleIssueAdvance('remediation_complete');
                    } else {
                      handleIssueAdvance('remediation_in_progress');
                    }
                  }}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <Play className="h-4 w-4" />Start Execution
                </button>
              </div>
            )}

            {/* Progress bar */}
            {actionPlans.length > 0 && (() => {
              const done = actionPlans.filter(p => p.status === 'completed').length;
              const pct  = Math.round((done / actionPlans.length) * 100);
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-bold text-gray-900">Remediation Progress</h2>
                    <span className="text-sm font-semibold text-gray-700">{done} / {actionPlans.length} complete</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div className="bg-green-500 h-2.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  {done === actionPlans.length && done > 0 && (
                    <p className="mt-2 text-xs text-green-600 font-medium flex items-center gap-1">
                      <CheckCircle className="h-3.5 w-3.5" />All action plans complete
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Action plan list with execution controls */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <Wrench className="h-4 w-4 text-blue-600" />
                <h2 className="text-base font-bold text-gray-900">Action Plans</h2>
                {actionPlans.length > 0 && (
                  <span className="text-xs text-gray-400 ml-1">({actionPlans.length})</span>
                )}
              </div>

              {apLoading ? (
                <div className="px-6 py-8 text-center"><Loader2 className="h-5 w-5 text-gray-400 animate-spin mx-auto" /></div>
              ) : actionPlans.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-gray-400">No action plans yet. Add them in the Plan Definition tab.</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {actionPlans.map(ap => {
                    const corrective = parseApDescription(ap.description);
                    const isOverdue = ap.due_date && ap.status !== 'completed' && calendarDateToLocal(ap.due_date) < new Date();
                    const cfg = AP_STATUS_CONFIG[ap.status] || AP_STATUS_CONFIG.not_started;
                    const canExecute = ['plan_accepted', 'remediation_in_progress'].includes(issue.status);
                    return (
                      <div key={ap.id} className="px-6 py-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                                {React.createElement(cfg.icon, { className: 'h-3 w-3' })}
                                {cfg.label}
                              </span>
                              {isOverdue && (
                                <span className="text-xs font-medium text-red-500 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Overdue</span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-gray-900 mb-1">{ap.title}</p>
                            {corrective && (
                              <p className="text-xs text-gray-500"><span className="font-medium">Corrective Action:</span> {corrective}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                              {ap.owner_name && <span>{ap.owner_name}</span>}
                              {ap.due_date && (
                                <span className={isOverdue ? 'text-red-500 font-medium' : ''}>
                                  Due {formatCalendarDate(ap.due_date, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                            {ap.status === 'completed' && ap.completion_notes && (
                              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-3">
                                <CheckCircle className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
                                <p className="text-xs text-green-800">{ap.completion_notes}</p>
                              </div>
                            )}
                          </div>
                          {/* Execution action buttons */}
                          {canExecute && (
                            <div className="flex items-center gap-2 shrink-0">
                              {ap.status === 'not_started' && (
                                <button
                                  onClick={() => handleApStatusChange(ap.id, 'in_progress')}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                >
                                  <Play className="h-3 w-3" />Start
                                </button>
                              )}
                              {ap.status === 'in_progress' && (
                                <button
                                  onClick={() => openCompletionModal(ap)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                                >
                                  <CheckCircle className="h-3 w-3" />Mark Complete
                                </button>
                              )}
                              {ap.status !== 'completed' && (
                                <button
                                  onClick={() => { setApEvidenceRef(ap.id); evidenceFileRef.current?.click(); }}
                                  disabled={uploadingEvidence}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                  {uploadingEvidence && apEvidenceRef === ap.id
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : <Upload className="h-3 w-3" />}
                                  Add Files
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Proceed to Verification CTA */}
            {['remediation_in_progress', 'remediation_complete'].includes(issue.status) && (() => {
              const done = actionPlans.filter(p => p.status === 'completed').length;
              const allDone = actionPlans.length === 0 || done === actionPlans.length;
              return (
                <div className={`rounded-xl border px-6 py-4 flex items-center justify-between ${allDone ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-2">
                    {allDone ? <CheckCircle className="h-5 w-5 text-green-600" /> : <Wrench className="h-5 w-5 text-gray-400" />}
                    <div>
                      <p className={`text-sm font-semibold ${allDone ? 'text-green-800' : 'text-gray-500'}`}>
                        {allDone ? (actionPlans.length === 0 ? 'Ready for verification' : 'All action plans complete') : `${actionPlans.length - done} action plan(s) remaining`}
                      </p>
                      <p className={`text-xs ${allDone ? 'text-green-600' : 'text-gray-400'}`}>
                        {allDone ? 'Proceed to verification to confirm the control is effective.' : 'Complete all action plans to proceed.'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleProceedToVerification}
                    disabled={!allDone || advancing}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm shrink-0"
                  >
                    {advancing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    {advancing ? 'Saving…' : 'Proceed to Verification →'}
                  </button>
                </div>
              );
            })()}

            {/* Remediation already complete — offer to go to Verification step */}

          </div>
        )}

        {/* ── STEP 1: MANAGEMENT REVIEW ── */}
        {activeStep === 1 && activeSubTab === 'Management Review' && (
          <div className="space-y-6">

            {/* State transition banner (in_management_review) */}
            {issue.status === 'in_management_review' && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="h-4 w-4 text-blue-600" />
                  <h2 className="text-sm font-semibold text-blue-800">Management Review</h2>
                </div>
                <p className="text-xs text-blue-600 mb-4">Review the issue and decide whether to accept it for remediation or flag it for further discussion.</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleIssueAdvance('plan_accepted')}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 transition-colors shadow-sm"
                  >
                    <ThumbsUp className="h-4 w-4" />Accept Issue
                  </button>
                  <button
                    onClick={() => { setReqDiscNote(''); setReqDiscModal(true); }}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-amber-500 rounded-xl hover:bg-amber-600 transition-colors shadow-sm"
                  >
                    <MessageSquare className="h-4 w-4" />Requires Discussion
                  </button>
                </div>
              </div>
            )}

            {/* Prior discussion thread — shown when back in management review after rework */}
            {issue.status === 'in_management_review' && mgmtData.discussion_thread.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">Previous Discussion</h2>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {mgmtData.discussion_thread.map((entry, i) => (
                    <div key={i} className="border-l-2 border-amber-300 pl-3 py-1">
                      <p className="text-xs text-gray-400 mb-0.5">{entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      <p className="text-sm text-gray-700">{entry.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Discussion thread (in_discussion) */}
            {issue.status === 'in_discussion' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-amber-600" />
                    <h2 className="text-sm font-semibold text-amber-800">In Discussion</h2>
                    <span className="flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-100 border border-purple-200 rounded-full px-2 py-0.5">
                      <Zap className="h-3 w-3" />Escalated
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleIssueAdvance('plan_accepted')}
                      className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <ThumbsUp className="h-3.5 w-3.5" />Accept Based on Discussion
                    </button>
                    <button
                      onClick={() => handleIssueAdvance('rework')}
                      className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />Push to Rework
                    </button>
                  </div>
                </div>

                {/* Thread feed */}
                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {mgmtData.discussion_thread.length === 0 ? (
                    <p className="text-xs text-amber-600">No discussion notes yet.</p>
                  ) : (
                    mgmtData.discussion_thread.map((entry, i) => (
                      <div key={i} className="bg-white border border-amber-100 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-amber-500 mb-1">{entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                        <p className="text-sm text-gray-800">{entry.note}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Add note */}
                <div className="flex items-start gap-2">
                  <textarea
                    rows={2}
                    value={discussionNote}
                    onChange={e => setDiscussionNote(e.target.value)}
                    placeholder="Add a discussion note…"
                    className="flex-1 px-3 py-2 text-sm border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none bg-white"
                  />
                  <button
                    onClick={handleAddDiscussionNote}
                    disabled={!discussionNote.trim() || addingNote}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors shrink-0"
                  >
                    {addingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Add Note
                  </button>
                </div>
              </div>
            )}

            {/* Rework info banner */}
            {issue.status === 'rework' && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="h-4 w-4 text-orange-600" />
                  <h2 className="text-sm font-semibold text-orange-800">In Rework</h2>
                </div>
                <p className="text-xs text-orange-600 mb-3">Auditor is addressing feedback from the discussion. The issue will be resubmitted to management once updates are complete.</p>
                {/* Discussion thread (read-only) */}
                {mgmtData.discussion_thread.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {mgmtData.discussion_thread.map((entry, i) => (
                      <div key={i} className="bg-white border border-orange-100 rounded-lg px-3 py-2">
                        <p className="text-xs text-orange-400 mb-0.5">{entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                        <p className="text-sm text-gray-700">{entry.note}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Outcome banner — read-only, shown when review step is in the past */}
            {!['reviewed','in_management_review','in_discussion','rework'].includes(issue.status) && STATUS_TO_STAGE[issue.status] > 1 && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center gap-3">
                <ThumbsUp className="h-5 w-5 text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Plan Accepted</p>
                  <p className="text-xs text-green-600">Management accepted the issue and approved it for remediation.</p>
                </div>
              </div>
            )}

            {/* Discussion thread history (read-only, shown after resolution) */}
            {!['in_management_review','in_discussion','rework'].includes(issue.status) && mgmtData.discussion_thread.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">Discussion History</h2>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {mgmtData.discussion_thread.map((entry, i) => (
                    <div key={i} className="border-l-2 border-gray-200 pl-3 py-1">
                      <p className="text-xs text-gray-400 mb-0.5">{entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      <p className="text-sm text-gray-700">{entry.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Responder details */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-sm font-semibold text-gray-800 mb-4">Responder Details</h2>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1.5">Name</label>
                  <input
                    type="text"
                    value={mgmtData.responder_name}
                    onChange={e => setMgmtData(m => ({ ...m, responder_name: e.target.value }))}
                    onBlur={() => saveMgmtData(mgmtData)}
                    placeholder="Responder name"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1.5">Title / Role</label>
                  <input
                    type="text"
                    value={mgmtData.responder_title}
                    onChange={e => setMgmtData(m => ({ ...m, responder_title: e.target.value }))}
                    onBlur={() => saveMgmtData(mgmtData)}
                    placeholder="e.g. CFO"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1.5">Response Date</label>
                  <input
                    type="date"
                    value={mgmtData.response_date}
                    onChange={e => setMgmtData(m => ({ ...m, response_date: e.target.value }))}
                    onBlur={() => saveMgmtData(mgmtData)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                  />
                </div>
              </div>
            </div>

            {/* Response text */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-800">Management Response</h2>
                <button
                  type="button"
                  onClick={() => toggleLock('management_response')}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors ${isLocked('management_response') ? 'text-red-500 border-red-200 bg-red-50' : 'text-gray-400 border-gray-200 hover:text-blue-500 hover:border-blue-200'}`}
                >
                  {isLocked('management_response') ? <><Lock className="h-3 w-3" />Locked</> : <><Unlock className="h-3 w-3" />Editable</>}
                </button>
              </div>
              <textarea
                rows={8}
                readOnly={isLocked('management_response')}
                value={mgmtData.response_text}
                onChange={e => setMgmtData(m => ({ ...m, response_text: e.target.value }))}
                onBlur={() => saveMgmtData(mgmtData)}
                placeholder="Enter management's formal response to this audit finding..."
                className="w-full px-0 py-0 text-sm text-gray-800 bg-transparent border-none focus:outline-none resize-none placeholder-gray-400"
              />
            </div>
          </div>
        )}

        {/* ── STEP 3: VERIFICATION ── */}
        {activeStep === 3 && activeSubTab === 'Verification' && (
          <div className="space-y-6">

            {/* Read-only verification result shown when viewing history from closed/reopened state */}
            {(issue.status === 'closed' || issue.status === 'reopened') && (() => {
              const vr = (issue.source_data || {}).verification_run;
              if (!vr) return null;
              const failed = vr.outcome === 'failed';
              const isComment = vr.method === 'comment';
              return (
                <div className={`rounded-xl px-6 py-5 border ${failed ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {failed ? <ShieldX className="h-5 w-5 text-red-600" /> : <ShieldCheck className="h-5 w-5 text-emerald-600" />}
                    <h2 className={`text-base font-bold ${failed ? 'text-red-800' : 'text-emerald-800'}`}>
                      {failed ? 'Verification Failed' : 'Verification Passed'}
                    </h2>
                  </div>
                  {isComment ? (
                    <div className={`bg-white rounded-lg px-4 py-3 border ${failed ? 'border-red-200' : 'border-emerald-200'}`}>
                      <p className={`text-xs font-medium mb-1 ${failed ? 'text-red-600' : 'text-emerald-600'}`}>Verification Comment</p>
                      <p className="text-sm text-gray-700">{vr.comment}</p>
                    </div>
                  ) : (
                    <div className={`flex items-center gap-3 bg-white rounded-lg px-4 py-3 w-fit border ${failed ? 'border-red-200' : 'border-emerald-200'}`}>
                      {failed ? <ShieldX className="h-4 w-4 text-red-500 shrink-0" /> : <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />}
                      <div>
                        <p className={`text-xs font-medium ${failed ? 'text-red-600' : 'text-emerald-600'}`}>Test Run Used</p>
                        <p className="text-sm font-semibold text-gray-800">
                          Run #{vr.run_number}
                          {vr.overall_status && <span className="ml-2 text-gray-500 font-normal">— {vr.overall_status}</span>}
                          {vr.total_exceptions != null && <span className="ml-2 text-gray-400 font-normal text-xs">· {vr.total_exceptions} exceptions</span>}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* pending_verification */}
            {issue.status === 'pending_verification' && (
              <div className="bg-white rounded-xl border border-indigo-200">
                <div className="px-6 py-4 border-b border-indigo-100 flex items-center gap-2">
                  <FileSearch className="h-4 w-4 text-indigo-600" />
                  <h2 className="text-base font-bold text-gray-900">Verification</h2>
                </div>
                <div className="px-6 py-5 space-y-5">

                  {/* Method toggle */}
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
                    <button
                      type="button"
                      onClick={() => setVerifMethod('test_run')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${verifMethod === 'test_run' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <FileCheck className="h-3.5 w-3.5" />Link Test Run
                    </button>
                    <button
                      type="button"
                      onClick={() => setVerifMethod('comment')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${verifMethod === 'comment' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <MessageSquare className="h-3.5 w-3.5" />Add Comment
                    </button>
                  </div>

                  {/* Test run selector */}
                  {verifMethod === 'test_run' && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium mb-2">Select a test run proving the control now operates effectively</p>
                      {linkedTestRuns.length === 0 ? (
                        <button
                          onClick={loadTestRunsForVerification}
                          disabled={loadingTestRuns}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
                        >
                          {loadingTestRuns ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                          Load Test Runs
                        </button>
                      ) : !linkedVerifRun ? (
                        <div className="relative">
                          <select
                            className="w-full appearance-none px-3 py-2 pr-8 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                            defaultValue=""
                            onChange={e => {
                              const run = linkedTestRuns.find(r => String(r.test_run_number || r.id) === e.target.value);
                              if (run) setLinkedVerifRun(run);
                            }}
                          >
                            <option value="">Select a test run…</option>
                            {linkedTestRuns.map(run => (
                              <option key={run.id || run.test_run_number} value={run.test_run_number || run.id}>
                                Run #{run.test_run_number}{run.test_run_name ? ` · ${run.test_run_name}` : ''} — {run.overall_status || run.workflow_status}{run.total_exceptions != null ? ` (${run.total_exceptions} exceptions)` : ''}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                      ) : (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 flex items-center justify-between">
                          <div>
                            <p className="text-xs text-indigo-500 font-medium">Selected Test Run</p>
                            <p className="text-sm font-semibold text-indigo-900">
                              Run #{linkedVerifRun.test_run_number}{linkedVerifRun.test_run_name ? ` · ${linkedVerifRun.test_run_name}` : ''} — {linkedVerifRun.overall_status || linkedVerifRun.workflow_status}
                              {linkedVerifRun.total_exceptions != null && ` · ${linkedVerifRun.total_exceptions} exceptions`}
                            </p>
                          </div>
                          <button onClick={() => setLinkedVerifRun(null)} className="text-xs text-indigo-400 hover:text-indigo-600">Change</button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Comment input */}
                  {verifMethod === 'comment' && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium mb-2">Describe the verification outcome and evidence reviewed</p>
                      <textarea
                        rows={4}
                        value={verifComment}
                        onChange={e => setVerifComment(e.target.value)}
                        placeholder="e.g. Reviewed updated process documentation and re-performed walkthroughs. Control is now operating as designed with no exceptions noted."
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white resize-none placeholder-gray-400"
                      />
                    </div>
                  )}

                  {/* Action buttons */}
                  {(() => {
                    const canAct = verifMethod === 'test_run' ? !!linkedVerifRun : verifComment.trim().length > 0;
                    const buildVerifRun = (outcome) => verifMethod === 'test_run'
                      ? { method: 'test_run', run_number: linkedVerifRun.test_run_number, test_run_id: linkedVerifRun.test_run_id ?? null, overall_status: linkedVerifRun.overall_status, workflow_status: linkedVerifRun.workflow_status ?? null, total_exceptions: linkedVerifRun.total_exceptions ?? null, outcome }
                      : { method: 'comment', comment: verifComment.trim(), outcome };
                    const saveAndAdvance = async (outcome, status) => {
                      const verifRun = buildVerifRun(outcome);
                      const merged = { ...(issue.source_data || {}), verification_run: verifRun };
                      try {
                        await axiosInstance.put(`/api/sox-issues/${id}`, { source_data: merged });
                        setIssue(prev => ({ ...prev, source_data: merged }));
                      } catch (e) { console.error('Failed to save verification:', e); }
                      handleIssueAdvance(status);
                    };
                    return (
                      <div className="flex items-center gap-3 pt-1">
                        <button
                          onClick={() => saveAndAdvance('passed', 'verification_complete')}
                          disabled={!canAct}
                          title={!canAct ? (verifMethod === 'test_run' ? 'Select a test run first' : 'Enter a comment first') : ''}
                          className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                        >
                          <ShieldCheck className="h-4 w-4" />Mark Verification Complete
                        </button>
                        <button
                          onClick={() => saveAndAdvance('failed', 'verification_failed')}
                          disabled={!canAct}
                          title={!canAct ? (verifMethod === 'test_run' ? 'Select a test run first' : 'Enter a comment first') : ''}
                          className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                        >
                          <ShieldX className="h-4 w-4" />Mark Verification Failed
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* verification_complete */}
            {issue.status === 'verification_complete' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-6 py-5">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  <h2 className="text-base font-bold text-emerald-800">Verification Complete</h2>
                </div>
                {(() => {
                  const vr = (issue.source_data || {}).verification_run;
                  if (!vr) return null;
                  return vr.method === 'comment' ? (
                    <div className="bg-white border border-emerald-200 rounded-lg px-4 py-3 mb-4">
                      <p className="text-xs text-emerald-600 font-medium mb-1">Verification Comment</p>
                      <p className="text-sm text-gray-700">{vr.comment}</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 bg-white border border-emerald-200 rounded-lg px-4 py-3 mb-4 w-fit">
                      <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Verified with Test Run</p>
                        <p className="text-sm font-semibold text-gray-800">
                          Run #{vr.run_number}
                          {vr.overall_status && <span className="ml-2 text-gray-500 font-normal">— {vr.overall_status}</span>}
                          {vr.total_exceptions != null && <span className="ml-2 text-gray-400 font-normal text-xs">· {vr.total_exceptions} exceptions</span>}
                        </p>
                      </div>
                    </div>
                  );
                })()}
                <p className="text-sm text-emerald-700 mb-5">The control is operating effectively. Close the issue with a documented reason to complete the audit trail.</p>
                <button
                  onClick={() => { setCloseReason(''); setCloseModal(true); }}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gray-800 rounded-xl hover:bg-gray-900 transition-colors shadow-sm"
                >
                  <CheckCircle className="h-4 w-4" />Close Issue
                </button>
              </div>
            )}

            {/* verification_failed */}
            {issue.status === 'verification_failed' && (() => {
              const attemptCount = (() => {
                try { return JSON.parse(issue.source_data || '{}').verification_attempt_count || 0; }
                catch { return 0; }
              })();
              return (
                <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-5">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldX className="h-5 w-5 text-red-600" />
                    <h2 className="text-base font-bold text-red-800">Verification Failed</h2>
                    {attemptCount > 0 && (
                      <span className="text-xs text-red-500 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">Attempt #{attemptCount}</span>
                    )}
                  </div>
                  {attemptCount >= 2 && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-4">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-700">Multiple verification failures. Consider reviewing the severity classification (e.g., escalating from CD to SD or MW).</p>
                    </div>
                  )}
                  <p className="text-sm text-red-700 mb-5">The control did not pass verification. Re-open remediation to address the root cause, or close the issue with compensating controls documented.</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleReopenRemediation}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-orange-600 rounded-xl hover:bg-orange-700 transition-colors shadow-sm"
                    >
                      <RefreshCw className="h-4 w-4" />Re-open Remediation
                    </button>
                    <button
                      onClick={() => { setCloseReason(''); setCloseModal(true); }}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gray-600 rounded-xl hover:bg-gray-700 transition-colors shadow-sm"
                    >
                      <X className="h-4 w-4" />Close with Compensating Controls
                    </button>
                  </div>
                </div>
              );
            })()}

          </div>
        )}

        {/* ── STEP 4: CLOSED ── */}
        {activeStep === 4 && activeSubTab === 'Closed' && (
          <div className="space-y-6">
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-6 py-5">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="h-5 w-5 text-gray-500" />
                <h2 className="text-base font-bold text-gray-700">Issue Closed</h2>
              </div>
              {(() => {
                const sd = issue.source_data || {};
                const history = sd.closing_history;
                const latest  = sd.closing_reason;
                const entries = history?.length > 0 ? history : (latest ? [{ reason: latest }] : []);
                if (entries.length === 0) return <p className="text-sm text-gray-400 italic">No closing reason recorded.</p>;
                const ordinals = ['1st', '2nd', '3rd', '4th', '5th'];
                return (
                  <div className="space-y-3">
                    {entries.map((entry, i) => {
                      const reason   = typeof entry === 'string' ? entry : entry?.reason;
                      const closedAt = typeof entry === 'object' ? entry?.closed_at : null;
                      return (
                        <div key={i} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs text-gray-400 font-medium">
                              {entries.length === 1 ? 'Closing Reason' : `${ordinals[i] || `${i + 1}th`} Close`}
                            </p>
                            {closedAt && (
                              <p className="text-xs text-gray-400">
                                {new Date(closedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                {' · '}
                                {new Date(closedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                          <p className="text-sm text-gray-700">{reason}</p>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {activeSubTab === 'Reopened' && isReopened && (
          <div className="space-y-6">
            {/* Reopen reason banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-6 py-5">
              <div className="flex items-center gap-2 mb-4">
                <RefreshCw className="h-5 w-5 text-amber-600" />
                <h2 className="text-base font-bold text-amber-800">Issue Reopened — Post-Close Correction</h2>
              </div>
              <p className="text-sm text-amber-700 mb-4">
                This issue has been reopened for factual correction. You may edit the Overview fields and the Memo. The Remediation Plan is locked. Once corrections are complete, close the issue again.
              </p>
              {(() => {
                const reason = (issue.source_data || {}).reopen_reason;
                return reason ? (
                  <div className="bg-white border border-amber-200 rounded-lg px-4 py-3">
                    <p className="text-xs text-amber-600 font-medium mb-1">Reason for Re-opening</p>
                    <p className="text-sm text-gray-700">{reason}</p>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Close Issue CTA */}
            <div className="flex justify-end">
              <button
                onClick={() => { setCloseReason(''); setCloseModal(true); }}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gray-800 rounded-xl hover:bg-gray-900 transition-colors shadow-sm"
              >
                <CheckCircle className="h-4 w-4" />Close Issue
              </button>
            </div>
          </div>
        )}

        {/* Memo content is in the slide-in panel — opened via the Memo button in the header */}

      </div>

      {/* ── Evidence Slide-in Panel ── */}
      {showEvidencePanel && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowEvidencePanel(false)} />
          <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-2xl flex flex-col z-50">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <h2 className="text-base font-bold text-gray-900">Evidence &amp; Artifacts</h2>
              </div>
              <button onClick={() => setShowEvidencePanel(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {evidenceLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                </div>
              ) : evidence.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <FileText className="h-8 w-8 text-gray-200 mb-2" />
                  <p className="text-sm text-gray-400">No evidence files yet.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {evidence.map(f => (
                    <div key={f.id} className="flex items-center justify-between px-6 py-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{f.filename}</p>
                          <p className="text-xs text-gray-400">
                            {f.created_at ? new Date(f.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                            {f.file_size ? ` · ${formatBytes(f.file_size)}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!isClosed && (
                          <button onClick={() => handleDeleteEvidence(f.id)} className="p-1 text-gray-300 hover:text-red-500 rounded transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {!isClosed && (
              <div className="px-6 py-4 border-t border-gray-100">
                <button
                  onClick={() => { setApEvidenceRef(null); evidenceFileRef.current?.click(); }}
                  disabled={uploadingEvidence}
                  className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 transition-colors disabled:opacity-50"
                >
                  {uploadingEvidence ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Upload Evidence
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Memo Slide-in Panel ── */}
      {showMemoPanel && (
        <div className="fixed inset-0 z-40">
          {!memoExpanded && (
            <div className="absolute inset-0 bg-black/20" onClick={() => { setShowMemoPanel(false); setMemoExpanded(false); }} />
          )}
          <div className={memoExpanded
            ? "absolute inset-0 bg-white flex"
            : "absolute right-0 top-0 h-full w-[720px] bg-white shadow-2xl flex flex-col z-50"
          }>

            {/* ── Issue Context left panel (expanded mode only) ── */}
            {memoExpanded && showIssueContext && (
              <div className="w-[300px] border-r border-gray-200 flex flex-col shrink-0 bg-gray-50/40">
                <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-200 bg-white shrink-0">
                  <h3 className="text-sm font-semibold text-gray-800">Issue Context</h3>
                  <button onClick={() => setShowIssueContext(false)} className="text-gray-400 hover:text-gray-600">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
                  {/* ID + Title */}
                  <div>
                    <span className="text-xs font-mono font-semibold text-blue-600">{issue?.issue_id}</span>
                    <p className="text-sm font-semibold text-gray-900 mt-1 leading-snug">{issue?.title}</p>
                  </div>
                  {/* Status + escalation */}
                  <div className="flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 capitalize">
                      {(issue?.status || '').replace(/_/g, ' ')}
                    </span>
                    {issue?.is_escalated && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                        <Zap className="h-3 w-3" />Escalated
                      </span>
                    )}
                  </div>
                  {/* Severity */}
                  {fields.classification && (() => {
                    const cfg = SEVERITY_CONFIG[fields.classification];
                    return cfg ? (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Severity</p>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${cfg.bg} ${cfg.text}`}>
                          {cfg.short} — {cfg.label}
                        </span>
                      </div>
                    ) : null;
                  })()}
                  {/* Meta fields */}
                  <div className="space-y-3">
                    {[
                      { label: 'Department', value: fields.department },
                      { label: 'Category',   value: fields.category },
                      { label: 'Period',     value: fields.period },
                      { label: 'Assignee',   value: fields.assignee_name || null },
                    ].filter(r => r.value).map(({ label, value }) => (
                      <div key={label}>
                        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                        <p className="text-xs font-medium text-gray-800">{value}</p>
                      </div>
                    ))}
                  </div>
                  {/* COSO Components */}
                  {overview.coso_components?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">COSO</p>
                      <div className="flex flex-wrap gap-1">
                        {overview.coso_components.map(c => (
                          <span key={c} className="px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-200">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Recurring / Vintage */}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Recurring Finding</p>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${fields.is_repeat ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                        {fields.is_repeat ? 'Yes — Repeat' : 'No'}
                      </span>
                      {fields.is_repeat && issue?.vintage_count > 0 && (
                        <span className="text-xs text-amber-600 font-medium">{issue.vintage_count} prior year{issue.vintage_count > 1 ? 's' : ''}</span>
                      )}
                    </div>
                  </div>
                  {/* Summary of Deficiency */}
                  {(overview.summary_of_deficiency || issue?.summary_of_deficiency) && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Summary of Deficiency</p>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        {overview.summary_of_deficiency || issue?.summary_of_deficiency}
                      </p>
                    </div>
                  )}
                  {/* Root Cause Analysis */}
                  {overview.root_cause_analysis && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Root Cause Analysis</p>
                      <p className="text-xs text-gray-700 leading-relaxed">{overview.root_cause_analysis}</p>
                    </div>
                  )}
                  {/* Impact */}
                  {(overview.impact || overview.financial_impact) && (
                    <div className="space-y-3">
                      {overview.impact && (
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Impact</p>
                          <p className="text-xs text-gray-700 leading-relaxed">{overview.impact}</p>
                        </div>
                      )}
                      {overview.financial_impact && (
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Financial Impact</p>
                          <p className="text-xs text-gray-700 leading-relaxed">{overview.financial_impact}</p>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Linked Controls */}
                  {linkedControls.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Linked Controls</p>
                      <div className="space-y-1.5">
                        {linkedControls.map(c => (
                          <div key={c.sox_control_id || c.id} className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                            <p className="text-xs font-semibold text-indigo-700">{c.control_id}</p>
                            {c.title && <p className="text-xs text-gray-500 mt-0.5 leading-snug">{c.title}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Linked Test Runs */}
                  {(overview.linked_test_runs || []).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Linked Test Runs</p>
                      <div className="space-y-1.5">
                        {(overview.linked_test_runs || []).map((run, i) => {
                          const statusColor = run.overall_status === 'failed'  ? 'text-red-600 bg-red-50 border-red-200'
                                            : run.overall_status === 'warning' ? 'text-amber-600 bg-amber-50 border-amber-200'
                                            : 'text-gray-600 bg-gray-50 border-gray-200';
                          return (
                            <div key={i} className="rounded-lg bg-white border border-gray-200 px-3 py-2 space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-700">{run.control_id} — Run #{run.run_number}</span>
                                {run.overall_status && (
                                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full border capitalize ${statusColor}`}>
                                    {run.overall_status}
                                  </span>
                                )}
                              </div>
                              {(run.total_exceptions != null || run.total_population != null) && (
                                <p className="text-xs text-gray-500">
                                  {run.total_exceptions != null && <span>{run.total_exceptions} exception{run.total_exceptions !== 1 ? 's' : ''}</span>}
                                  {run.total_exceptions != null && run.total_population != null && <span className="mx-1">·</span>}
                                  {run.total_population != null && <span>pop. {run.total_population}</span>}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Main memo panel ── */}
            <div className={`flex flex-col ${memoExpanded ? 'flex-1 min-w-0' : 'h-full'}`}>
            {/* Panel header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <div>
                <h2 className="text-base font-bold text-gray-900">Audit Finding Memo</h2>
                <p className="text-xs text-gray-400 mt-0.5">PCAOB-style memo. All sections auto-save on blur.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAIDraftMemo}
                  disabled={memoLoading || isClosed}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {memoLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {memoLoading ? 'Drafting…' : 'AI Draft'}
                </button>
                <button
                  onClick={handleExportMemoPdf}
                  disabled={pdfExporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {pdfExporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  PDF
                </button>
                <button
                  onClick={handleExportMemoDocx}
                  disabled={docxExporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {docxExporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  Word
                </button>
                {memoExportError && (
                  <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-lg">{memoExportError}</span>
                )}
                {/* Context panel toggle — only in expanded mode */}
                {memoExpanded && (
                  <button
                    onClick={() => setShowIssueContext(v => !v)}
                    title={showIssueContext ? 'Hide issue context' : 'Show issue context'}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {showIssueContext ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
                  </button>
                )}
                {/* Expand / minimize toggle */}
                <button
                  onClick={() => { setMemoExpanded(v => !v); if (!memoExpanded) setShowIssueContext(true); }}
                  title={memoExpanded ? 'Restore panel' : 'Expand to full screen'}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {memoExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                <button onClick={() => { setShowMemoPanel(false); setMemoExpanded(false); }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Panel body — scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

              {/* Memo header */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Memo Header</h3>
                <div className="space-y-3">
                  {[
                    { key: 'memo_to',      label: 'To',      placeholder: 'e.g. HR Department Head' },
                    { key: 'memo_from',    label: 'From',    placeholder: 'e.g. Internal Audit' },
                    { key: 'memo_subject', label: 'Subject', placeholder: issue?.title || 'Enter subject…' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key} className="flex items-center gap-4">
                      <span className="w-16 text-xs font-semibold text-gray-500 shrink-0">{label}:</span>
                      <input
                        type="text"
                        value={memoData[key]}
                        onChange={e => !isClosed && setMemoData(m => ({ ...m, [key]: e.target.value }))}
                        onBlur={() => !isClosed && saveMemoData(memoData)}
                        readOnly={isClosed}
                        placeholder={placeholder}
                        className={`flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500${isClosed ? ' bg-gray-50 text-gray-500' : ''}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Description of Deficiency (5 C's) */}
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-800">Description of Deficiency</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Condition · Criteria · Cause · Effect · Corrective Action</p>
                </div>
                {[
                  { key: 'condition',         label: 'Condition',           placeholder: 'What was observed during testing? Reference specific exception counts, populations, and dates.' },
                  { key: 'criteria',          label: 'Criteria',            placeholder: 'The applicable standard, policy, or control requirement that was not met.' },
                  { key: 'cause',             label: 'Cause',               placeholder: 'Root cause narrative — address both type (design/operating) and category (people/process/technology).' },
                  { key: 'consequence',       label: 'Consequence / Effect', placeholder: 'Quantified impact — dollar exposure, exception rate, financial statement impact.' },
                  { key: 'corrective_action', label: 'Corrective Action',   placeholder: "Summary of management's remediation plan." },
                ].map(({ key, label, placeholder }, idx, arr) => (
                  <div key={key} className={`px-5 py-4 ${idx < arr.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
                      <button
                        type="button"
                        onClick={() => toggleLock(`memo_${key}`)}
                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors ${isLocked(`memo_${key}`) ? 'text-red-500 border-red-200 bg-red-50' : 'text-gray-400 border-gray-200 hover:text-blue-500 hover:border-blue-200'}`}
                      >
                        {isLocked(`memo_${key}`) ? <><Lock className="h-3 w-3" />Locked</> : <><Unlock className="h-3 w-3" />Editable</>}
                      </button>
                    </div>
                    <RichTextEditor
                      minRows={5}
                      readOnly={isClosed || isLocked(`memo_${key}`)}
                      value={memoData[key]}
                      onChange={html => !isClosed && setMemoData(m => ({ ...m, [key]: html }))}
                      onBlur={() => !isClosed && saveMemoData(memoData)}
                      placeholder={placeholder}
                    />
                  </div>
                ))}
              </div>

              {/* Additional PCAOB sections */}
              {[
                { key: 'risk_factors',         label: 'Risk Factors',          placeholder: 'Factors that elevate or mitigate the severity of this deficiency.' },
                { key: 'exposed_amounts',       label: 'Exposed Amounts',       placeholder: 'Specific dollar exposure and population details.' },
                { key: 'magnitude_assessment',  label: 'Magnitude Assessment',  placeholder: 'Whether a reasonable possibility exists that this deficiency could result in a material misstatement.' },
                { key: 'compensating_controls', label: 'Compensating Controls', placeholder: 'Any compensating controls and whether they are effective.' },
                { key: 'recommendation',        label: 'Recommendation',        placeholder: "Auditor's formal recommendation to management." },
              ].map(({ key, label, placeholder }) => (
                <div key={key} className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">{label}</h3>
                  <RichTextEditor
                    minRows={4}
                    readOnly={isClosed}
                    value={memoData[key]}
                    onChange={html => !isClosed && setMemoData(m => ({ ...m, [key]: html }))}
                    onBlur={() => !isClosed && saveMemoData(memoData)}
                    placeholder={placeholder}
                  />
                </div>
              ))}

              {/* Reportable to SEC */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Reportable to SEC</h3>
                <p className="text-xs text-gray-400 mb-4">Auditor determination required for final reporting.</p>
                <div className="flex gap-3">
                  {[
                    { value: true,  label: 'Yes',       color: 'border-red-400 bg-red-50 text-red-700'      },
                    { value: false, label: 'No',         color: 'border-green-400 bg-green-50 text-green-700' },
                    { value: null,  label: 'Undecided',  color: 'border-gray-300 bg-gray-50 text-gray-600'   },
                  ].map(opt => (
                    <button
                      key={String(opt.value)}
                      onClick={() => { if (isClosed) return; const next = { ...memoData, reportable_to_sec: opt.value }; setMemoData(next); saveMemoData(next); }}
                      disabled={isClosed}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all disabled:opacity-60 disabled:cursor-default ${memoData.reportable_to_sec === opt.value ? opt.color : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* MW Indicators */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-1">
                  <h3 className="text-sm font-semibold text-gray-800">Material Weakness Indicators</h3>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">PCAOB AS 2201.69</span>
                </div>
                <p className="text-xs text-gray-400 mb-4">Check all indicators that apply to this finding.</p>
                <div className="space-y-1">
                  {(memoData.mw_indicators || DEFAULT_MW_INDICATORS).map((ind) => {
                    const meta = MW_INDICATORS.find(m => m.id === ind.indicator_id);
                    return (
                      <div key={ind.indicator_id} className={`rounded-lg border transition-all ${ind.checked ? 'border-amber-300 bg-amber-50' : 'border-transparent'}`}>
                        <div className="flex items-start gap-3 px-3 py-2">
                          <button
                            onClick={() => !isClosed && toggleMwIndicator(ind.indicator_id)}
                            disabled={isClosed}
                            className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors disabled:opacity-60 disabled:cursor-default ${ind.checked ? 'bg-amber-500 border-amber-500' : 'border-gray-300 hover:border-amber-400'}`}
                          >
                            {ind.checked && <CheckCircle className="h-3 w-3 text-white" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${ind.checked ? 'font-semibold text-amber-800' : 'text-gray-700'}`}>{ind.label || meta?.label}</p>
                            {ind.checked && (
                              <input
                                type="text"
                                value={ind.rationale || ''}
                                onChange={e => !isClosed && updateMwRationale(ind.indicator_id, e.target.value)}
                                onBlur={() => !isClosed && saveMwRationale()}
                                readOnly={isClosed}
                                placeholder="Rationale (optional)"
                                className="mt-1.5 w-full text-xs text-gray-700 bg-white border border-amber-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Memo file upload */}
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Upload className="h-4 w-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-gray-800">Manually Uploaded Memo</h3>
                  </div>
                  {!isClosed && (
                    <button
                      onClick={() => memoFileRef.current?.click()}
                      disabled={parsingMemoFile}
                      className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 transition-colors disabled:opacity-50"
                    >
                      {parsingMemoFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      {parsingMemoFile ? 'Extracting…' : 'Upload Memo'}
                    </button>
                  )}
                </div>
                {evidence.filter(f => f.file_type === 'memo').length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <FileText className="h-7 w-7 text-gray-200 mb-2" />
                    <p className="text-sm text-gray-400">No manually uploaded memo yet.</p>
                    <p className="text-xs text-gray-300 mt-0.5">Upload a .pdf or .docx to attach alongside the AI draft.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {evidence.filter(f => f.file_type === 'memo').map(f => (
                      <div key={f.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-4 w-4 text-blue-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{f.filename}</p>
                            <p className="text-xs text-gray-400">
                              {f.created_at ? new Date(f.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                            </p>
                          </div>
                        </div>
                        {!isClosed && (
                          <button onClick={() => handleDeleteEvidence(f.id)} className="p-1 text-gray-300 hover:text-red-500 rounded transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Memo Upload Confirm Modal ─────────────────────────────────────── */}
      {pendingMemoFile && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={() => { setPendingMemoFile(null); setParseError(''); }} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto">
              <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Extract &amp; Overwrite Memo Fields?</h3>
                  <p className="text-sm text-gray-500 mt-0.5 truncate max-w-xs">{pendingMemoFile.name}</p>
                </div>
                <button onClick={() => { setPendingMemoFile(null); setParseError(''); }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-3">
                <p className="text-sm text-gray-700">
                  The AI will read this file and extract memo content (To, From, Subject, Condition, Criteria, Cause, Effect, and all other sections) into the memo fields.
                </p>
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700">
                    Any fields successfully extracted will <strong>overwrite</strong> what you currently have. Fields not found in the document are left unchanged.
                  </p>
                </div>
                {parseError && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{parseError}</p>
                )}
              </div>
              <div className="flex justify-end gap-3 px-6 pb-6">
                <button
                  onClick={() => { setPendingMemoFile(null); setParseError(''); }}
                  disabled={parsingMemoFile}
                  className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={processMemoFileUpload}
                  disabled={parsingMemoFile}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-40 transition-colors"
                >
                  {parsingMemoFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {parsingMemoFile ? 'Extracting…' : 'Extract & Overwrite'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Completion Modal ──────────────────────────────────────────────── */}
      {completionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={() => setCompletionModal(null)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto">
              <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Mark Complete</h3>
                  <p className="text-sm text-gray-500 mt-0.5 truncate max-w-xs">{completionModal.apTitle}</p>
                </div>
                <button onClick={() => setCompletionModal(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    What was done? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={4}
                    value={completionNotes}
                    onChange={e => setCompletionNotes(e.target.value)}
                    placeholder="Describe the remediation action taken…"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    autoFocus
                  />
                  {!completionNotes.trim() && (
                    <p className="text-xs text-red-500 mt-1">Completion notes are required.</p>
                  )}
                </div>
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                  <p className="text-xs text-amber-700">
                    Consider uploading supporting evidence via <strong>Add Files</strong> on the action plan before completing.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 pb-6">
                <button onClick={() => setCompletionModal(null)} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={submitCompletion}
                  disabled={!completionNotes.trim() || completionSubmitting}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-40 transition-colors"
                >
                  {completionSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  {completionSubmitting ? 'Saving…' : 'Mark Done'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Close Issue Modal ── */}
      {closeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Close Issue</h3>
              <button onClick={() => setCloseModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-gray-600">Provide a reason for closing this issue. This will be recorded in the audit trail.</p>
              <textarea
                rows={4}
                value={closeReason}
                onChange={e => setCloseReason(e.target.value)}
                placeholder="Closing reason (required)…"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button onClick={() => setCloseModal(false)} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
              <button
                onClick={handleCloseIssue}
                disabled={!closeReason.trim() || closingIssue}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-gray-800 rounded-xl hover:bg-gray-900 disabled:opacity-40 transition-colors"
              >
                {closingIssue ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                {closingIssue ? 'Closing…' : 'Close Issue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Re-open Modal ── */}
      {reopenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Re-open Issue</h3>
              <button onClick={() => setReopenModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-gray-600">
                Re-opening allows post-close correction of findings, classification, COSO mapping, and the memo. The remediation plan will be locked. Provide a reason for re-opening.
              </p>
              <textarea
                rows={4}
                value={reopenReason}
                onChange={e => setReopenReason(e.target.value)}
                placeholder="Reason for re-opening (required)…"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button onClick={() => setReopenModal(false)} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
              <button
                onClick={handleReopenIssue}
                disabled={!reopenReason.trim() || reopeningIssue}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-amber-600 rounded-xl hover:bg-amber-700 disabled:opacity-40 transition-colors"
              >
                {reopeningIssue ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {reopeningIssue ? 'Reopening…' : 'Re-open Issue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Clone Confirmation Modal ── */}
      {showCloneConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Clone Issue</h3>
              <button onClick={() => setShowCloneConfirm(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600">
                This will create a new draft issue titled <span className="font-medium text-gray-900">"Copy of {issue?.title}"</span> with the same classification, department, and overview content. Action plans and evidence will not be copied.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button onClick={() => setShowCloneConfirm(false)} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
              <button
                onClick={() => { setShowCloneConfirm(false); handleCloneIssue(); }}
                disabled={cloningIssue}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-40 transition-colors"
              >
                {cloningIssue ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                Clone Issue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Requires Discussion Modal ── */}
      {reqDiscModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Flag for Discussion</h3>
              <button onClick={() => setReqDiscModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-gray-600">Describe what needs to be discussed or clarified before this issue can be accepted. This note will start the discussion thread.</p>
              <textarea
                rows={4}
                value={reqDiscNote}
                onChange={e => setReqDiscNote(e.target.value)}
                placeholder="Discussion note (required)…"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                autoFocus
              />
              <p className="text-xs text-amber-600 flex items-center gap-1"><Zap className="h-3 w-3" />This issue will be automatically escalated.</p>
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button onClick={() => setReqDiscModal(false)} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
              <button
                onClick={handleRequiresDiscussion}
                disabled={!reqDiscNote.trim() || reqDiscSubmitting}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-amber-500 rounded-xl hover:bg-amber-600 disabled:opacity-40 transition-colors"
              >
                {reqDiscSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                {reqDiscSubmitting ? 'Sending…' : 'Flag for Discussion'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
